<template>
  <div class="panel global-score rounded ai-analytics-panel">
    <div v-if="!hasData" class="ai-empty">
      <i class="fa fa-info-circle"></i>
      No Action Tracker data matches the current filters.
    </div>

    <div v-else>
      <div class="ai-report-header">
        <div>
          <h3>AI GENERATED DATA ANALYTICS</h3>
          <div class="subtitle">{{ drillThroughSubtitle }}</div>
        </div>

        <button
            v-if="isDrillThrough || selectedMetricDashboard"
            class="btn-ai-secondary"
            @click="handleHeaderBackClick"
        >
          <i class="fa fa-arrow-left"></i>
          Back to overview
        </button>
      </div>

      <div class="drill-breadcrumb">
        <button
            type="button"
            :class="{ active: !isDrillThrough && !selectedMetricDashboard }"
            @click="clearDrillThrough"
        >
          Overview
        </button>

        <span v-if="selectedRegulatoryKey">
          /
          <button
              type="button"
              :class="{ active: selectedRegulatoryKey && !selectedBusinessFunction }"
              @click="selectRegulatoryKey(selectedRegulatoryKey)"
          >
            {{ selectedRegulatoryKey }}
          </button>
        </span>

        <span v-if="selectedBusinessFunction">
          /
          <strong>{{ selectedBusinessFunction }}</strong>
        </span>

        <span v-if="selectedMetricDashboard">
          /
          <strong>{{ metricDashboardTitle }}</strong>
        </span>
      </div>

      <div class="chatbot-intro">
        <img src="/images/mo-chat.png" alt="">
        <span>
          Welcome to Model Office AI generated data dashboards enabling your firm to assess and scrutinise performance across all key metrics, drill down into granular data points to enable data driven compliance reporting.
        </span>
      </div>

      <div
          v-if="returnedAction"
          :class="['returned-action-banner', `returned-action-${returnedAction.state}`]"
      >
        <i :class="returnedAction.icon"></i>

        <div>
          <strong>{{ returnedAction.title }}</strong>
          <span>{{ returnedAction.question }}</span>
          <span v-if="returnedAction.hint" class="returned-action-hint">{{ returnedAction.hint }}</span>
        </div>

        <button
            type="button"
            class="returned-action-close"
            aria-label="Dismiss"
            @click="$emit('dismiss-returned-action')"
        >
          <i class="fa fa-times"></i>
        </button>
      </div>

      <div class="scope-banner">
        <div>
          <strong>{{ drillThroughTitle }}</strong>
          <span>{{ scopeInsight }}</span>
        </div>

        <div class="scope-count">
          {{ formatNumber(currentScopeAnswers.length) }} records
        </div>
      </div>

      <div v-if="selectedMetricDashboard" class="row ai-dashboard-layout metric-dashboard-layout">
        <div class="col-md-12 col-sm-12 col-xs-12 ai-centre-column">
          <div class="metric-dashboard-header">
            <div>
              <strong>{{ metricDashboardTitle }}</strong>
              <span>{{ metricDashboardSubtitle }}</span>
            </div>

            <button
                type="button"
                class="btn-ai-secondary"
                @click="clearMetricDashboard"
            >
              <i class="fa fa-arrow-left"></i>
              Back to analytics overview
            </button>
          </div>

          <div class="metric-dashboard-tabs">
            <button
                type="button"
                :class="{ active: selectedMetricDashboard === 'audit_actions' }"
                @click="openMetricDashboard('audit_actions')"
            >Audit actions</button>
            <button
                type="button"
                :class="{ active: selectedMetricDashboard === 'answer_options' }"
                @click="openMetricDashboard('answer_options')"
            >Questions & answer options</button>
            <button
                type="button"
                :class="{ active: selectedMetricDashboard === 'notes_actions' }"
                @click="openMetricDashboard('notes_actions')"
            >Notes & actions</button>
            <button
                type="button"
                :class="{ active: selectedMetricDashboard === 'target_remedy_dates' }"
                @click="openMetricDashboard('target_remedy_dates')"
            >Target remedy dates</button>
          </div>

          <div v-if="selectedMetricDashboard === 'audit_actions'" class="ai-section metric-detail-section diagnostic-section">
            <div class="section-title">Audit Actions Performance Dashboard</div>

            <div class="block-explainer">
              <div><strong>What this shows:</strong> A focused dashboard for answered, completed, positively answered, blank and overdue audit actions.</div>
            </div>

            <div class="diagnostic-card-grid metric-dashboard-card-grid">
              <div
                  class="diagnostic-card"
                  v-for="item in diagnosticSummaryCards"
                  :key="item.label"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <em :class="item.statusClass">{{ item.note }}</em>
                <small>{{ item.calculation }}</small>
              </div>
            </div>

            <div class="chart-card audit-actions-performance-chart large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Audit Actions Performance</strong>
                  <span>Answered, completed, positively answered, blank and overdue actions in the selected data.</span>
                </div>
              </div>

              <div class="simple-bar-chart large-simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in diagnosticBarChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, diagnosticBarChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>
                  <div class="simple-bar-label">{{ item.label }}</div>
                </div>
              </div>
            </div>

            <div class="chart-card rag-breakdown-card large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Overdue Actions by RAG Rating</strong>
                  <span>Overdue audit actions grouped by regulatory risk severity.</span>
                </div>
              </div>

              <div class="rag-breakdown-grid">
                <div
                    class="rag-card rag-red"
                >
                  <span>Red</span>
                  <strong>{{ formatNumber(overdueRagBreakdown.red) }}</strong>
                </div>

                <div
                    class="rag-card rag-amber"
                >
                  <span>Amber</span>
                  <strong>{{ formatNumber(overdueRagBreakdown.amber) }}</strong>
                </div>

                <div
                    class="rag-card rag-green"
                >
                  <span>Green</span>
                  <strong>{{ formatNumber(overdueRagBreakdown.green) }}</strong>
                </div>
              </div>
            </div>

            <questions-performance-block
                :questions="questionsForScope"
                :format-number="formatNumber"
            />

            <div class="ai-section metric-ai-insights-section">
              <div class="section-title action-title-row">
                <span>{{ metricDashboardAiTitle }}</span>

                <button
                    type="button"
                    class="btn-regenerate-ai"
                    :disabled="isLoadingAiActionPoints"
                    @click="requestAiActionPoints(true)"
                >
                  <i :class="isLoadingAiActionPoints ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                  {{ isLoadingAiActionPoints ? 'Generating...' : 'Regenerate' }}
                </button>
              </div>
              <div v-if="aiGeneratedLabel && !isLoadingAiActionPoints" class="ai-generated-at">{{ aiGeneratedLabel }}</div>

              <div class="block-explainer">
                <div>
                  <strong>What this shows:</strong>
                  AI-generated observations and practical recommendations based only on the currently selected dashboard data.
                </div>
                <div>
                  <strong>How to use:</strong>
                  Review each recommendation and use its source link to open the related dashboard or Action Tracker filter.
                </div>
              </div>

              <div v-if="isLoadingAiActionPoints" class="ai-loading-state">
                <i class="fa fa-spinner fa-spin"></i>
                <strong>Generating AI insights</strong>
                <p>Analysing the selected dashboard data. This may take a few seconds.</p>

                <div class="ai-skeleton-line"></div>
                <div class="ai-skeleton-line short"></div>
                <div class="ai-skeleton-line"></div>
              </div>

              <div v-else-if="aiActionPointsError" class="ai-error-state">
                <i class="fa fa-exclamation-triangle"></i>
                {{ aiActionPointsError }}
              </div>

              <div v-else class="ai-insight-columns">
                <ai-insight-list
                    title="Strengths"
                    icon="fa fa-check-circle"
                    :items="aiActionPoints.strengths"
                    empty-text="No AI strengths were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />

                <ai-insight-list
                    title="Weaknesses"
                    icon="fa fa-exclamation-circle"
                    :items="aiActionPoints.weaknesses"
                    empty-text="No AI weaknesses were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />
              </div>
            </div>
          </div>

          <div v-else-if="selectedMetricDashboard === 'answer_options'" class="ai-section metric-detail-section">
            <div class="section-title">Questions and Answer Options Performance Dashboard</div>

            <div class="block-explainer">
              <div><strong>What this shows:</strong> Answer distribution and question performance for the selected Action Tracker scope.</div>
            </div>

            <div class="chart-card answer-options-performance-chart large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Question Answers and Answer Options Performance</strong>
                  <span>Answer option volume and percentage of total selected answers.</span>
                </div>
              </div>

              <div class="simple-bar-chart large-simple-bar-chart answer-options-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in answerOptionsBarChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, answerOptionsBarChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>
                  <div class="simple-bar-label">{{ item.label }}</div>
                </div>
              </div>
            </div>

            <div class="chart-card rag-breakdown-card large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Answer Options by RAG Rating</strong>
                  <span>All selected answers grouped by Red, Amber and Green rating.</span>
                </div>
              </div>

              <div class="rag-breakdown-grid">
                <div
                    class="rag-card rag-red"
                >
                  <span>Red</span>
                  <strong>{{ formatNumber(answerOptionsRagBreakdown.red) }}</strong>
                </div>

                <div
                    class="rag-card rag-amber"
                >
                  <span>Amber</span>
                  <strong>{{ formatNumber(answerOptionsRagBreakdown.amber) }}</strong>
                </div>

                <div
                    class="rag-card rag-green"
                >
                  <span>Green</span>
                  <strong>{{ formatNumber(answerOptionsRagBreakdown.green) }}</strong>
                </div>
              </div>
            </div>

            <answer-options-block
                :items="answerOptionsForScope"
                title="Question Answers and Answer Options Performance"
                subtitle="Answer option distribution for the selected dashboard scope."
                :format-number="formatNumber"
            />

            <div class="ai-section metric-ai-insights-section">
              <div class="section-title action-title-row">
                <span>{{ metricDashboardAiTitle }}</span>

                <button
                    type="button"
                    class="btn-regenerate-ai"
                    :disabled="isLoadingAiActionPoints"
                    @click="requestAiActionPoints(true)"
                >
                  <i :class="isLoadingAiActionPoints ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                  {{ isLoadingAiActionPoints ? 'Generating...' : 'Regenerate' }}
                </button>
              </div>
              <div v-if="aiGeneratedLabel && !isLoadingAiActionPoints" class="ai-generated-at">{{ aiGeneratedLabel }}</div>

              <div class="block-explainer">
                <div>
                  <strong>What this shows:</strong>
                  AI-generated observations and practical recommendations based only on the currently selected dashboard data.
                </div>
                <div>
                  <strong>How to use:</strong>
                  Review each recommendation and use its source link to open the related dashboard or Action Tracker filter.
                </div>
              </div>

              <div v-if="isLoadingAiActionPoints" class="ai-loading-state">
                <i class="fa fa-spinner fa-spin"></i>
                <strong>Generating AI insights</strong>
                <p>Analysing the selected dashboard data. This may take a few seconds.</p>

                <div class="ai-skeleton-line"></div>
                <div class="ai-skeleton-line short"></div>
                <div class="ai-skeleton-line"></div>
              </div>

              <div v-else-if="aiActionPointsError" class="ai-error-state">
                <i class="fa fa-exclamation-triangle"></i>
                {{ aiActionPointsError }}
              </div>

              <div v-else class="ai-insight-columns">
                <ai-insight-list
                    title="Strengths"
                    icon="fa fa-check-circle"
                    :items="aiActionPoints.strengths"
                    empty-text="No AI strengths were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />

                <ai-insight-list
                    title="Weaknesses"
                    icon="fa fa-exclamation-circle"
                    :items="aiActionPoints.weaknesses"
                    empty-text="No AI weaknesses were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />
              </div>
            </div>
          </div>

          <div v-else-if="selectedMetricDashboard === 'notes_actions'" class="ai-section metric-detail-section tracker-link-section">
            <div class="section-title">Notes and Actions Dashboard</div>

            <div class="block-explainer">
              <div><strong>What this shows:</strong> Notes, documents and evidence coverage across completed and outstanding actions.</div>
            </div>

            <div class="chart-card notes-actions-chart large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Notes and Actions Performance</strong>
                  <span>Questions with notes, without notes and documents added in the selected data.</span>
                </div>
              </div>

              <div class="simple-bar-chart large-simple-bar-chart notes-actions-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in notesActionsChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, notesActionsChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>
                  <div class="simple-bar-label">{{ item.label }}</div>
                </div>
              </div>
            </div>

            <notes-documents-block
                :notes="notesForScope"
                :documents-count="documentsCountForScope"
                :format-number="formatNumber"
                :format-display-value="formatDisplayValue"
            />

            <div class="chart-card documents-by-function-card large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Documents Added by Business Function</strong>
                  <span>Business functions with at least one document in the selected data.</span>
                </div>
              </div>

              <div
                  v-if="documentsByBusinessFunctionWithDocuments.length"
                  class="doc-function-dropdown"
              >
                <button
                    type="button"
                    class="doc-function-dropdown-toggle"
                    @click="docFunctionDropdownOpen = !docFunctionDropdownOpen"
                >
                  <span>{{ documentsByBusinessFunctionWithDocuments.length }} business function{{ documentsByBusinessFunctionWithDocuments.length === 1 ? '' : 's' }} with documents</span>
                  <i :class="docFunctionDropdownOpen ? 'fa fa-chevron-up' : 'fa fa-chevron-down'"></i>
                </button>

                <div v-if="docFunctionDropdownOpen" class="doc-function-dropdown-list">
                  <div
                      class="doc-function-dropdown-item"
                      v-for="item in documentsByBusinessFunctionWithDocuments"
                      :key="`${item.regulatoryKey}-${item.businessFunction}`"
                  >
                    <strong>{{ item.businessFunction }}</strong>
                    <span>{{ item.regulatoryKey }}</span>
                    <em>{{ formatNumber(item.documents) }} document{{ item.documents === 1 ? '' : 's' }}</em>
                  </div>
                </div>
              </div>

              <div v-else class="ai-empty-small">
                No business functions with documents in the selected scope.
              </div>
            </div>

            <div class="ai-section metric-ai-insights-section">
              <div class="section-title action-title-row">
                <span>{{ metricDashboardAiTitle }}</span>

                <button
                    type="button"
                    class="btn-regenerate-ai"
                    :disabled="isLoadingAiActionPoints"
                    @click="requestAiActionPoints(true)"
                >
                  <i :class="isLoadingAiActionPoints ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                  {{ isLoadingAiActionPoints ? 'Generating...' : 'Regenerate' }}
                </button>
              </div>
              <div v-if="aiGeneratedLabel && !isLoadingAiActionPoints" class="ai-generated-at">{{ aiGeneratedLabel }}</div>

              <div class="block-explainer">
                <div>
                  <strong>What this shows:</strong>
                  AI-generated observations and practical recommendations based only on the currently selected dashboard data.
                </div>
                <div>
                  <strong>How to use:</strong>
                  Review each recommendation and use its source link to open the related dashboard or Action Tracker filter.
                </div>
              </div>

              <div v-if="isLoadingAiActionPoints" class="ai-loading-state">
                <i class="fa fa-spinner fa-spin"></i>
                <strong>Generating AI insights</strong>
                <p>Analysing the selected dashboard data. This may take a few seconds.</p>

                <div class="ai-skeleton-line"></div>
                <div class="ai-skeleton-line short"></div>
                <div class="ai-skeleton-line"></div>
              </div>

              <div v-else-if="aiActionPointsError" class="ai-error-state">
                <i class="fa fa-exclamation-triangle"></i>
                {{ aiActionPointsError }}
              </div>

              <div v-else class="ai-insight-columns">
                <ai-insight-list
                    title="Strengths"
                    icon="fa fa-check-circle"
                    :items="aiActionPoints.strengths"
                    empty-text="No AI strengths were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />

                <ai-insight-list
                    title="Weaknesses"
                    icon="fa fa-exclamation-circle"
                    :items="aiActionPoints.weaknesses"
                    empty-text="No AI weaknesses were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />
              </div>
            </div>
          </div>

          <div v-else-if="selectedMetricDashboard === 'target_remedy_dates'" class="ai-section metric-detail-section tracker-link-section">
            <div class="section-title">Target Remedy Dates Dashboard</div>

            <div class="block-explainer">
              <div><strong>What this shows:</strong> Targeted, completed, outstanding and overdue remedy date performance for the selected data.</div>
            </div>

            <div class="chart-card target-remedy-chart large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Target Remedy Dates Performance</strong>
                  <span>Target remedy completion and outstanding status in the selected data.</span>
                </div>
              </div>

              <div class="simple-bar-chart large-simple-bar-chart target-remedy-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in targetRemedyChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, targetRemedyChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>
                  <div class="simple-bar-label">{{ item.label }}</div>
                </div>
              </div>
            </div>

            <div class="chart-card rag-breakdown-card large-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Outstanding Remedy Items by RAG Rating</strong>
                  <span>Outstanding (not yet completed) items grouped by regulatory risk severity.</span>
                </div>
              </div>

              <div class="rag-breakdown-grid">
                <div
                    class="rag-card rag-red"
                >
                  <span>Red</span>
                  <strong>{{ formatNumber(outstandingRagBreakdown.red) }}</strong>
                </div>

                <div
                    class="rag-card rag-amber"
                >
                  <span>Amber</span>
                  <strong>{{ formatNumber(outstandingRagBreakdown.amber) }}</strong>
                </div>

                <div
                    class="rag-card rag-green"
                >
                  <span>Green</span>
                  <strong>{{ formatNumber(outstandingRagBreakdown.green) }}</strong>
                </div>
              </div>
            </div>

            <remedy-dates-block
                :items="remedyDatesForScope"
                :insight="remedyDatesInsight"
                :format-number="formatNumber"
            />

            <div class="ai-section metric-ai-insights-section">
              <div class="section-title action-title-row">
                <span>{{ metricDashboardAiTitle }}</span>

                <button
                    type="button"
                    class="btn-regenerate-ai"
                    :disabled="isLoadingAiActionPoints"
                    @click="requestAiActionPoints(true)"
                >
                  <i :class="isLoadingAiActionPoints ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                  {{ isLoadingAiActionPoints ? 'Generating...' : 'Regenerate' }}
                </button>
              </div>
              <div v-if="aiGeneratedLabel && !isLoadingAiActionPoints" class="ai-generated-at">{{ aiGeneratedLabel }}</div>

              <div class="block-explainer">
                <div>
                  <strong>What this shows:</strong>
                  AI-generated observations and practical recommendations based only on the currently selected dashboard data.
                </div>
                <div>
                  <strong>How to use:</strong>
                  Review each recommendation and use its source link to open the related dashboard or Action Tracker filter.
                </div>
              </div>

              <div v-if="isLoadingAiActionPoints" class="ai-loading-state">
                <i class="fa fa-spinner fa-spin"></i>
                <strong>Generating AI insights</strong>
                <p>Analysing the selected dashboard data. This may take a few seconds.</p>

                <div class="ai-skeleton-line"></div>
                <div class="ai-skeleton-line short"></div>
                <div class="ai-skeleton-line"></div>
              </div>

              <div v-else-if="aiActionPointsError" class="ai-error-state">
                <i class="fa fa-exclamation-triangle"></i>
                {{ aiActionPointsError }}
              </div>

              <div v-else class="ai-insight-columns">
                <ai-insight-list
                    title="Strengths"
                    icon="fa fa-check-circle"
                    :items="aiActionPoints.strengths"
                    empty-text="No AI strengths were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />

                <ai-insight-list
                    title="Weaknesses"
                    icon="fa fa-exclamation-circle"
                    :items="aiActionPoints.weaknesses"
                    empty-text="No AI weaknesses were generated for this dashboard."
                    @select-source="handleAiActionPointSource"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="!isDrillThrough" class="row ai-dashboard-layout">
        <div class="col-md-8 col-sm-12 col-xs-12 ai-centre-column">
          <div class="ai-section performance-highlight-section">
            <div class="section-title">
              Regulatory Keys and Business Functions Performance
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Best performance and areas for improvement across Regulatory Keys and related Business Functions.
              </div>
              <div>
                <strong>How to use:</strong>
                Click any item below to open the relevant Regulatory Key or Business Function results dashboard.
              </div>
            </div>


            <div class="chart-card regulatory-performance-chart">
              <div class="chart-header">
                <div>
                  <strong>Regulatory Performance: Best vs Worst by Business Function</strong>
                  <span>Best and weakest business function score inside each Regulatory Key.</span>
                </div>
              </div>

              <div class="grouped-bar-chart">
                <div
                    class="grouped-bar-item"
                    v-for="item in regulatoryPerformanceChartGroups"
                    :key="item.regulatoryKey"
                >
                  <div class="bar-pair">
                    <button
                        type="button"
                        class="chart-bar best"
                        :title="item.bestFunction"
                        :style="{ height: chartBarHeight(item.bestScore, 100) }"
                        @click.stop="selectBusinessFunction(item.regulatoryKey, item.bestFunction)"
                    >
                      <span>{{ formatSectionScore(item.bestScore) }}</span>
                    </button>

                    <button
                        type="button"
                        class="chart-bar weakest"
                        :title="item.worstFunction"
                        :style="{ height: chartBarHeight(item.worstScore, 100) }"
                        @click.stop="selectBusinessFunction(item.regulatoryKey, item.worstFunction)"
                    >
                      <span>{{ formatSectionScore(item.worstScore) }}</span>
                    </button>
                  </div>

                  <div class="bar-caption">
                    <strong>{{ item.regulatoryKey }}</strong>
                    <small>Best: {{ item.bestFunction }}</small>
                    <small>Weakest: {{ item.worstFunction }}</small>
                  </div>
                </div>
              </div>

              <div class="chart-legend">
                <span><i class="legend-best"></i> Best business function</span>
                <span><i class="legend-weakest"></i> Area for improvement</span>
              </div>
            </div>

            <div class="performance-card-grid">
              <div
                  class="performance-card clickable"
                  v-for="item in overallPerformanceCards"
                  :key="item.label"
                  @click="handlePerformanceCardClick(item)"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.name }}</strong>
                <em :class="item.statusClass">{{ item.score }}</em>
              </div>
            </div>

            <div class="mini-action-list">
              <div
                  class="mini-action-item"
                  v-for="(point, index) in regulatoryPerformanceActionPoints"
                  :key="index"
              >
                <span>{{ index + 1 }}</span>
                <p>{{ point }}</p>
              </div>
            </div>
          </div>

          <div class="ai-section">
            <div class="section-title">
              Regulatory Key Overview Dashboards
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Overview dashboard cards for each Regulatory Key and their related business functions.
              </div>
              <div>
                <strong>How to use:</strong>
                Click a Regulatory Key to open its dashboard. Click a business function to drill down one level deeper.
              </div>
            </div>

            <p v-if="hasNarrowingFilter && !overviewRegulatoryKeys.length" class="steer-empty">
              No business functions match the current filters.
            </p>

            <div
                class="regulatory-row clickable"
                v-for="item in overviewRegulatoryKeys"
                :key="item.name"
                @click="selectRegulatoryKey(item.name)"
            >
              <div class="regulatory-main">
                <div>
                  <strong>{{ item.name }}</strong>
                  <span>{{ item.description }}</span>
                </div>

                <div class="score-value">
                  {{ formatSectionScore(item.score) }}
                </div>
              </div>

              <div class="score-bar">
                <div
                    :class="['score-fill', scoreClass(item.score)]"
                    :style="{ width: item.score + '%' }"
                ></div>
              </div>

              <div class="module-grid">
                <div
                    :class="['module-item', 'clickable', { 'no-records': module.records === 0 }]"
                    v-for="module in item.modules"
                    :key="module.name"
                    @click.stop="selectBusinessFunction(item.name, module.name)"
                >
                  <div class="module-name">
                    {{ module.name }}
                  </div>

                  <div class="module-score">
                    {{ formatSectionScore(module.score) }}
                  </div>

                  <div class="mini-bar">
                    <div
                        :class="['mini-fill', scoreClass(module.score)]"
                        :style="{ width: module.score + '%' }"
                    ></div>
                  </div>

                  <div v-if="module.records === 0" class="no-records-note">
                    No actions in current filters
                  </div>

                  <div v-if="module.total !== null && module.total !== undefined" class="module-meta">
                    {{ formatNumber(module.total) }} answers
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4 col-sm-12 col-xs-12 ai-right-column">

          <div class="ai-section action-section">
            <div class="section-title action-title-row">
              <span>5 AI Action Points</span>

              <button
                  type="button"
                  class="btn-regenerate-ai"
                  :disabled="isLoadingAiActionPoints"
                  @click="requestAiActionPoints(true)"
              >
                <i :class="isLoadingAiActionPoints ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                {{ isLoadingAiActionPoints ? 'Generating...' : 'Regenerate' }}
              </button>
            </div>
            <div v-if="aiGeneratedLabel && !isLoadingAiActionPoints" class="ai-generated-at">{{ aiGeneratedLabel }}</div>

            <div v-if="isLoadingAiActionPoints" class="ai-loading-state">
              <i class="fa fa-spinner fa-spin"></i>
              <strong>Generating AI action points</strong>
              <p>Analysing the selected Action Tracker data. This may take a few seconds.</p>

              <div class="ai-skeleton-line"></div>
              <div class="ai-skeleton-line short"></div>
              <div class="ai-skeleton-line"></div>
            </div>

            <div v-else-if="aiActionPointsError" class="ai-error-state">
              <i class="fa fa-exclamation-triangle"></i>
              {{ aiActionPointsError }}
            </div>

            <ai-insight-list
                v-else
                :items="aiActionPoints.items"
                :empty-text="aiActionPointsEmptyText"
                @select-source="handleAiActionPointSource"
            />
          </div>


          <div class="ai-section diagnostic-section">
            <div class="section-title">
              Diagnostic Question Data and Audit Actions Performance
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Question and action performance across completed actions, positively answered actions, blank responses and overdue actions.
              </div>
              <div>
                <strong>Accuracy rate:</strong>
                This is the percentage of selected actions answered positively or assessed as a good outcome.
              </div>
            </div>

            <div class="right-dashboard-link-row">
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('audit_actions')"
              >
                <i class="fa fa-bar-chart"></i>
                Open audit actions dashboard
              </button>
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('answer_options')"
              >
                <i class="fa fa-pie-chart"></i>
                Open questions and answer options dashboard
              </button>
            </div>

            <div class="diagnostic-card-grid">
              <div
                  class="diagnostic-card"
                  v-for="item in diagnosticSummaryCards"
                  :key="item.label"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <em :class="item.statusClass">{{ item.note }}</em>
                <small>{{ item.calculation }}</small>
              </div>
            </div>


            <div class="chart-card audit-actions-performance-chart">
              <div class="chart-header">
                <div>
                  <strong>Audit Actions Performance</strong>
                  <span>Answered, completed and positively answered actions in the selected data.</span>
                </div>
              </div>

              <div class="simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in diagnosticBarChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, diagnosticBarChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>

                  <div class="simple-bar-label">
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>

            <questions-performance-block
                :questions="questionsForScope"
                :format-number="formatNumber"
            />
          </div>

          <div class="metric-link-wrapper">
            <button
                type="button"
                class="inline-dashboard-link standalone-dashboard-link"
                @click="openMetricDashboard('answer_options')"
            >
              <i class="fa fa-pie-chart"></i>
              Open questions and answer options dashboard
            </button>

            <answer-options-block
                :items="answerOptionsForScope"
                title="Question Answers and Answer Options Performance"
                subtitle="Answer option distribution for the overview data, including positive, partial, negative and blank answer patterns."
                :format-number="formatNumber"
            />
          </div>

          <div class="ai-section tracker-link-section">
            <div class="section-title">
              Notes and Actions
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Action Tracker notes, documents and evidence coverage for selected records.
              </div>
              <div>
                <strong>SBG note:</strong>
                If auditor note data is available, include auditor notes and compare the ratio against firm notes.
              </div>
            </div>


            <div class="right-dashboard-link-row">
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('notes_actions')"
              >
                <i class="fa fa-bar-chart"></i>
                Open notes and actions dashboard
              </button>
            </div>

            <div class="chart-card notes-actions-chart compact-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Notes and Actions Performance</strong>
                  <span>Notes, no-notes and document evidence coverage.</span>
                </div>
              </div>

              <div class="simple-bar-chart compact-simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in notesActionsChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, notesActionsChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>

                  <div class="simple-bar-label">
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>
            <notes-documents-block
                :notes="notesForScope"
                :documents-count="documentsCountForScope"
                :format-number="formatNumber"
                :format-display-value="formatDisplayValue"
            />
          </div>

          <div class="ai-section tracker-link-section">
            <div class="section-title">
              Target Remedy Dates
            </div>


            <div class="right-dashboard-link-row">
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('target_remedy_dates')"
              >
                <i class="fa fa-bar-chart"></i>
                Open target remedy dates dashboard
              </button>
            </div>

            <div class="chart-card target-remedy-chart compact-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Target Remedy Dates Performance</strong>
                  <span>Targeted, completed, outstanding and overdue remedy items.</span>
                </div>
              </div>

              <div class="simple-bar-chart compact-simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in targetRemedyChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, targetRemedyChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>

                  <div class="simple-bar-label">
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>
            <remedy-dates-block
                :items="remedyDatesForScope"
                :insight="remedyDatesInsight"
                :format-number="formatNumber"
            />
          </div>
        </div>
      </div>

      <div v-else class="row ai-dashboard-layout">
        <div class="col-md-8 col-sm-12 col-xs-12 ai-centre-column">
          <div class="scope-kpi-grid">
            <div
                class="scope-kpi-card"
                v-for="item in scopeKpiCards"
                :key="item.label"
            >
              <span>{{ item.label }}</span>
              <strong>{{ item.value }}</strong>
              <em>{{ item.note }}</em>
            </div>
          </div>

          <div
              v-if="selectedRegulatoryKey && !selectedBusinessFunction"
              class="ai-section"
          >
            <div class="section-title">
              {{ selectedRegulatoryKey }} Business Functions
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Drill-through performance for each business function inside {{ selectedRegulatoryKey }}.
              </div>
              <div>
                <strong>How to use:</strong>
                Click a business function to open its own dashboard.
              </div>
            </div>

            <div class="business-function-grid">
              <div
                  :class="['business-function-card', 'clickable', { 'no-records': item.records === 0 }]"
                  v-for="item in visibleScopedBusinessFunctions"
                  :key="item.name"
                  @click="selectBusinessFunction(selectedRegulatoryKey, item.name)"
              >
                <div>
                  <strong>{{ item.name }}</strong>
                  <span v-if="item.total !== null && item.total !== undefined">{{ formatNumber(item.total) }} answers</span>
                  <span v-if="item.records === 0" class="no-records-note">No actions in current filters</span>
                </div>

                <div :class="['circle-score', scoreClass(item.score)]">
                  {{ formatSectionScore(item.score) }}
                </div>
              </div>
            </div>

            <p v-if="hasNarrowingFilter && !visibleScopedBusinessFunctions.length" class="steer-empty">
              No business functions match the current filters.
            </p>
          </div>

          <div class="ai-section">
            <div class="section-title">
              Top Dynamic Steers / Areas To Address
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Highest priority outstanding actions in the selected area.
              </div>
              <div>
                <strong>Calculation:</strong>
                Priority is based on severity, overdue status, missing target remedy date, missing notes and missing documents.
              </div>
            </div>

            <div
                class="dynamic-steer"
                v-for="(item, index) in topDynamicSteers"
                :key="index"
            >
              <div :class="['steer-dot', severityClass(item.severity)]"></div>

              <div class="steer-content">
                <strong>{{ index + 1 }}. {{ item.businessFunction }}</strong>

                <p>
                  <span>Question Action:</span>
                  <a
                      v-if="item.questionId"
                      href="#"
                      class="steer-action-link"
                      @click.prevent="openAction(item)"
                  >{{ item.question }}</a>
                  <template v-else>{{ item.question }}</template>
                </p>

                <p>
                  <span>Your Answer:</span>
                  {{ item.answer }}
                </p>

                <p v-if="item.targetRemedy">
                  <span>Target Remedy:</span>
                  {{ item.targetRemedy }}
                </p>

                <button
                    v-if="item.questionId"
                    type="button"
                    class="steer-open-action"
                    @click="openAction(item)"
                >
                  <i class="fa fa-external-link"></i>
                  Address this action
                </button>

                <a
                    v-if="item.resourceTitle"
                    :href="item.resourceUrl || '#'"
                    target="_blank"
                    rel="noopener"
                    class="resource-link"
                >
                  <i class="fa fa-plus-circle"></i>
                  {{ item.resourceTitle }}
                </a>
              </div>
            </div>

            <p v-if="!topDynamicSteers.length" class="steer-empty">
              No outstanding actions in this area.
            </p>

            <div v-if="awaitingSignOffActions.length" class="awaiting-sign-off">
              <div class="awaiting-sign-off-title">
                <i class="fa fa-check-circle"></i>
                Resolved — awaiting sign-off ({{ formatNumber(awaitingSignOffActions.length) }})
              </div>

              <p class="awaiting-sign-off-hint">
                These actions now have a positive answer but their status is not Completed yet.
                Open each action and mark it as Completed (a person responsible and all checkpoints are required).
              </p>

              <div
                  class="dynamic-steer awaiting-sign-off-item"
                  v-for="(item, index) in awaitingSignOffActions.slice(0, 5)"
                  :key="`awaiting-${index}`"
              >
                <div class="steer-dot awaiting-sign-off-dot"></div>

                <div class="steer-content">
                  <strong>{{ item.businessFunction }}</strong>

                  <p>
                    <span>Question Action:</span>
                    {{ item.question }}
                  </p>

                  <p>
                    <span>Your Answer:</span>
                    {{ item.answer }}
                  </p>

                  <button
                      v-if="item.questionId"
                      type="button"
                      class="steer-open-action awaiting-sign-off-cta"
                      @click="openAction(item)"
                  >
                    <i class="fa fa-check"></i>
                    Mark as Completed
                  </button>
                </div>
              </div>

              <p v-if="awaitingSignOffActions.length > 5" class="awaiting-sign-off-more">
                and {{ formatNumber(awaitingSignOffActions.length - 5) }} more awaiting sign-off
              </p>
            </div>
          </div>

          <div class="ai-section">
            <div class="section-title">
              Best / Worst Question Performance
            </div>

            <div class="two-column-table">
              <div>
                <h5>Questions with highest score</h5>

                <table class="table ai-table">
                  <thead>
                  <tr>
                    <th>Question Text</th>
                    <th class="text-right">Score</th>
                  </tr>
                  </thead>

                  <tbody>
                  <tr
                      v-for="item in topQuestions"
                      :key="item.question"
                  >
                    <td>{{ item.question }}</td>
                    <td class="text-right">{{ formatQuestionScore(item.score) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>

              <div>
                <h5>Questions with lowest score</h5>

                <table class="table ai-table">
                  <thead>
                  <tr>
                    <th>Question Text</th>
                    <th class="text-right">Score</th>
                  </tr>
                  </thead>

                  <tbody>
                  <tr
                      v-for="item in bottomQuestions"
                      :key="item.question"
                  >
                    <td>{{ item.question }}</td>
                    <td class="text-right">{{ formatQuestionScore(item.score) }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        <div class="col-md-4 col-sm-12 col-xs-12 ai-right-column">

          <div class="ai-section action-section">
            <div class="section-title action-title-row">
              <span>5 AI Action Points</span>

              <button
                  type="button"
                  class="btn-regenerate-ai"
                  :disabled="isLoadingAiActionPoints"
                  @click="requestAiActionPoints(true)"
              >
                <i :class="isLoadingAiActionPoints ? 'fa fa-spinner fa-spin' : 'fa fa-refresh'"></i>
                {{ isLoadingAiActionPoints ? 'Generating...' : 'Regenerate' }}
              </button>
            </div>
            <div v-if="aiGeneratedLabel && !isLoadingAiActionPoints" class="ai-generated-at">{{ aiGeneratedLabel }}</div>

            <div v-if="isLoadingAiActionPoints" class="ai-loading-state">
              <i class="fa fa-spinner fa-spin"></i>
              <strong>Generating AI action points</strong>
              <p>Analysing the selected Action Tracker data. This may take a few seconds.</p>

              <div class="ai-skeleton-line"></div>
              <div class="ai-skeleton-line short"></div>
              <div class="ai-skeleton-line"></div>
            </div>

            <div v-else-if="aiActionPointsError" class="ai-error-state">
              <i class="fa fa-exclamation-triangle"></i>
              {{ aiActionPointsError }}
            </div>

            <ai-insight-list
                v-else
                :items="aiActionPoints.items"
                :empty-text="aiActionPointsEmptyText"
                @select-source="handleAiActionPointSource"
            />
          </div>


          <div class="ai-section diagnostic-section">
            <div class="section-title">
              Diagnostic Question Data and Audit Actions Performance
            </div>

            <div class="block-explainer">
              <div>
                <strong>What this shows:</strong>
                Question and action performance for the selected drill-through data.
              </div>
              <div>
                <strong>Accuracy rate:</strong>
                This is the percentage of selected actions answered positively or assessed as a good outcome.
              </div>
            </div>

            <div class="right-dashboard-link-row">
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('audit_actions')"
              >
                <i class="fa fa-bar-chart"></i>
                Open audit actions dashboard
              </button>
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('answer_options')"
              >
                <i class="fa fa-pie-chart"></i>
                Open questions and answer options dashboard
              </button>
            </div>

            <div class="diagnostic-card-grid">
              <div
                  class="diagnostic-card"
                  v-for="item in diagnosticSummaryCards"
                  :key="item.label"
              >
                <span>{{ item.label }}</span>
                <strong>{{ item.value }}</strong>
                <em :class="item.statusClass">{{ item.note }}</em>
                <small>{{ item.calculation }}</small>
              </div>
            </div>


            <div class="chart-card audit-actions-performance-chart">
              <div class="chart-header">
                <div>
                  <strong>Audit Actions Performance</strong>
                  <span>Answered, completed and positively answered actions in the selected data.</span>
                </div>
              </div>

              <div class="simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in diagnosticBarChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, diagnosticBarChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>

                  <div class="simple-bar-label">
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>

            <questions-performance-block
                :questions="questionsForScope"
                :format-number="formatNumber"
            />
          </div>

          <div class="metric-link-wrapper">
            <button
                type="button"
                class="inline-dashboard-link standalone-dashboard-link"
                @click="openMetricDashboard('answer_options')"
            >
              <i class="fa fa-pie-chart"></i>
              Open questions and answer options dashboard
            </button>

            <answer-options-block
                :items="answerOptionsForScope"
                title="Question Answers and Answer Options Performance"
                subtitle="Answer distribution for the selected area."
                :format-number="formatNumber"
            />
          </div>

          <div class="ai-section tracker-link-section">
            <div class="section-title">
              Notes and Actions
            </div>


            <div class="right-dashboard-link-row">
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('notes_actions')"
              >
                <i class="fa fa-bar-chart"></i>
                Open notes and actions dashboard
              </button>
            </div>

            <div class="chart-card notes-actions-chart compact-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Notes and Actions Performance</strong>
                  <span>Notes, no-notes and document evidence coverage.</span>
                </div>
              </div>

              <div class="simple-bar-chart compact-simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in notesActionsChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, notesActionsChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>

                  <div class="simple-bar-label">
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>
            <notes-documents-block
                :notes="notesForScope"
                :documents-count="documentsCountForScope"
                :format-number="formatNumber"
                :format-display-value="formatDisplayValue"
            />
          </div>

          <div class="ai-section tracker-link-section">
            <div class="section-title">
              Target Remedy Dates
            </div>


            <div class="right-dashboard-link-row">
              <button
                  type="button"
                  class="inline-dashboard-link"
                  @click="openMetricDashboard('target_remedy_dates')"
              >
                <i class="fa fa-bar-chart"></i>
                Open target remedy dates dashboard
              </button>
            </div>

            <div class="chart-card target-remedy-chart compact-chart-card">
              <div class="chart-header">
                <div>
                  <strong>Target Remedy Dates Performance</strong>
                  <span>Targeted, completed, outstanding and overdue remedy items.</span>
                </div>
              </div>

              <div class="simple-bar-chart compact-simple-bar-chart">
                <div
                    class="simple-bar-item"
                    v-for="item in targetRemedyChartItems"
                    :key="item.label"
                >
                  <div class="simple-bar-track">
                    <div
                        :class="['simple-bar-fill', item.class]"
                        :style="{ height: chartBarHeight(item.value, targetRemedyChartMaxValue) }"
                    >
                      <span>{{ formatNumber(item.value) }}</span>
                    </div>
                  </div>

                  <div class="simple-bar-label">
                    {{ item.label }}
                  </div>
                </div>
              </div>
            </div>
            <remedy-dates-block
                :items="remedyDatesForScope"
                :insight="remedyDatesInsight"
                :format-number="formatNumber"
            />
          </div>

          <best-worst-block
              :items="bestWorstCards"
              title="Regulatory Keys and Business Functions Performance"
          />

          <div class="ai-section">
            <div class="section-title">
              Top 3 Outstanding Actions
            </div>

            <div
                class="outstanding-action"
                v-for="(item, index) in topOutstandingActions"
                :key="index"
            >
              <div :class="['small-severity-dot', severityClass(item.severity)]"></div>

              <div>
                <strong>{{ item.businessFunction }}</strong>
                <p>{{ item.question }}</p>
                <em>{{ item.answer }}</em>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import AnswerOptionsBlock from './action-tracker-ai/AnswerOptionsBlock.vue';
import QuestionsPerformanceBlock from './action-tracker-ai/QuestionsPerformanceBlock.vue';
import NotesDocumentsBlock from './action-tracker-ai/NotesDocumentsBlock.vue';
import RemedyDatesBlock from './action-tracker-ai/RemedyDatesBlock.vue';
import BestWorstBlock from './action-tracker-ai/BestWorstBlock.vue';
import AiInsightList from './action-tracker-ai/AiInsightList.vue';

// Later replace null with your backend route, for example:
// const AI_ACTION_POINTS_ENDPOINT = '/action-tracker/ai/action-points';
const AI_ACTION_POINTS_ENDPOINT = '/action-tracker/ai/action-points';

const METRIC_DASHBOARDS = ['audit_actions', 'answer_options', 'notes_actions', 'target_remedy_dates'];





export default {
  components: {
    AnswerOptionsBlock,
    QuestionsPerformanceBlock,
    NotesDocumentsBlock,
    RemedyDatesBlock,
    BestWorstBlock,
    AiInsightList
  },

  props: {
    severityGroups: {
      type: Array,
      default: () => []
    },
    selectedFilters: {
      type: Object,
      default: () => ({})
    },
    groupBySeverity: {
      type: Boolean,
      default: true
    },
    enabledparentcategories: {
      type: [Array, Object, String],
      default: () => []
    },
    initialMetricDashboard: {
      type: String,
      default: null
    },
    // question_id of the action the user opened from this dashboard and
    // came back from (URL param aiFrom).
    returnedFromAction: {
      type: [String, Number],
      default: null
    },
  },

  emits: ['close', 'drill-change', 'metric-dashboard-change', 'open-action', 'dismiss-returned-action'],

  data() {
    return {
      // Drill-through state mirrors the Regulatory Key / Business Function
      // filters of the Action Tracker filter bar (see applyFiltersToDrill).
      selectedRegulatoryKey: null,
      selectedBusinessFunction: null,
      selectedMetricDashboard: METRIC_DASHBOARDS.includes(this.initialMetricDashboard)
          ? this.initialMetricDashboard
          : null,
      ignoreNextFilterDrillChange: false,

      aiActionPoints: { weaknesses: [], strengths: [], items: [] },
      isLoadingAiActionPoints: false,
      aiActionPointsError: null,
      aiActionRequestId: 0,
      aiActionPointsTimer: null,
      docFunctionDropdownOpen: false
    };
  },

  mounted() {
    this.requestAiActionPoints();
  },

  beforeDestroy() {
    this.clearAiActionPointsTimer();
  },

  watch: {
    // Declared before aiActionScopeSignature so a drill-through restored from
    // the URL on load doesn't trigger a second AI request.
    filterDrillSignature: {
      handler() {
        this.applyFiltersToDrill();
      },
      immediate: true
    },

    aiActionScopeSignature() {
      this.requestAiActionPoints();
    }
  },

  computed: {
    visibleAnswers() {
      return this.severityGroups.reduce((answers, group) => {
        const groupAnswers = group.answers || [];

        return answers.concat(groupAnswers.map(answer => {
          return Object.assign({}, answer, {
            _severityTitle: group.title || null
          });
        }));
      }, []);
    },

    hasData() {
      return this.visibleAnswers.length > 0 || this.enabledParentCategoryTree.length > 0;
    },

    returnedAction() {
      if (!this.returnedFromAction) {
        return null;
      }

      const answer = this.visibleAnswers.find(item => String(item.question_id) === String(this.returnedFromAction));

      if (!answer) {
        return null;
      }

      const question = this.getQuestionText(answer);

      if (this.isCompleted(answer)) {
        return {
          state: 'signed-off',
          icon: 'fa fa-check-circle',
          title: 'Action signed off',
          question,
          hint: null
        };
      }

      if (this.isResolved(answer)) {
        return {
          state: 'resolved',
          icon: 'fa fa-check',
          title: 'Action resolved — awaiting sign-off',
          question,
          hint: 'Set its status to Completed in the Action Tracker to sign it off.'
        };
      }

      return {
        state: 'outstanding',
        icon: 'fa fa-exclamation-circle',
        title: 'Action still outstanding',
        question,
        hint: 'It stays in Top Dynamic Steers until it is answered positively or marked as Completed.'
      };
    },

    filterDrillSignature() {
      return JSON.stringify([
        this.activeFilterName('parent'),
        this.activeFilterName('section')
      ]);
    },

    isDrillThrough() {
      return !!this.selectedRegulatoryKey || !!this.selectedBusinessFunction;
    },

    aiDashboardKind() {
      if (this.selectedMetricDashboard) {
        return this.selectedMetricDashboard;
      }

      if (this.selectedBusinessFunction) {
        return 'business_function';
      }

      if (this.selectedRegulatoryKey) {
        return 'regulatory_key';
      }

      return 'overview';
    },

    currentScopeAnswers() {
      return this.visibleAnswers.filter(answer => {
        const regulatoryKeyMatches = !this.selectedRegulatoryKey
            || this.getRegulatoryKey(answer) === this.selectedRegulatoryKey;

        const businessFunctionMatches = !this.selectedBusinessFunction
            || this.getBusinessFunction(answer) === this.selectedBusinessFunction;

        return regulatoryKeyMatches && businessFunctionMatches;
      });
    },

    allEnabledParentCategoryTree() {
      return this.normaliseEnabledParentCategories(this.enabledparentcategories);
    },

    allEnabledBusinessFunctionSections() {
      return this.allEnabledParentCategoryTree.reduce((sections, parent) => {
        return sections.concat(parent.children || []);
      }, []);
    },

    // Every enabled Regulatory Key and Business Function is always shown with
    // its assessment score, even when the current filters leave it without
    // Action Tracker records (e.g. a function that is 100% correct). Record
    // counts come from sectionRecordCounts; tiles with none are muted.
    enabledParentCategoryTree() {
      return this.allEnabledParentCategoryTree;
    },

    // Action Tracker records per business function (section id) left after
    // the current filters.
    sectionRecordCounts() {
      const counts = {};

      this.visibleAnswers.forEach(answer => {
        const section = this.findEnabledBusinessFunctionMeta(answer);

        if (section && section.id !== null && section.id !== undefined) {
          counts[String(section.id)] = (counts[String(section.id)] || 0) + 1;
        }
      });

      return counts;
    },

    enabledBusinessFunctionSections() {
      return this.enabledParentCategoryTree.reduce((sections, parent) => {
        return sections.concat(parent.children || []);
      }, []);
    },

    aiActionScopeSignature() {
      if (!this.hasData) {
        return 'empty';
      }

      return JSON.stringify({
        regulatoryKey: this.selectedRegulatoryKey,
        businessFunction: this.selectedBusinessFunction,
        metricDashboard: this.selectedMetricDashboard,
        selectedFilters: this.selectedFilters,
        answered: this.answeredCountForScope,
        completed: this.completedCountForScope,
        resolved: this.resolvedCountForScope,
        correct: this.correctlyAnsweredCountForScope,
        overdue: this.overdueCountForScope,
        targeted: this.targetedCountForScope,
        documents: this.documentsCountForScope,
        notes: this.totalNotesForScope,
        topOutstanding: this.topOutstandingActions.map(item => item.question)
      });
    },

    severityTitle() {
      if (!this.severityGroups.length) {
        return 'Action Tracker Analytics';
      }

      if (this.severityGroups.length === 1) {
        return this.severityGroups[0].title || 'Action Tracker Analytics';
      }

      return 'All Filtered Action Tracker Items';
    },

    metricDashboardTitle() {
      const titles = {
        audit_actions: 'Audit Actions Performance Dashboard',
        answer_options: 'Questions and Answer Options Performance Dashboard',
        notes_actions: 'Notes and Actions Dashboard',
        target_remedy_dates: 'Target Remedy Dates Dashboard'
      };

      return titles[this.selectedMetricDashboard] || 'Data Dashboard';
    },

    metricDashboardAiTitle() {
      const titles = {
        audit_actions: 'AI Insights for Audit Actions',
        answer_options: 'AI Insights for Questions and Answer Options',
        notes_actions: 'AI Insights for Notes and Actions',
        target_remedy_dates: 'AI Insights for Target Remedy Dates'
      };

      return titles[this.selectedMetricDashboard] || 'AI Insights and Recommendations';
    },

    metricDashboardSubtitle() {
      const subtitles = {
        audit_actions: 'Detailed diagnostic question data and audit action performance for the current scope.',
        answer_options: 'Detailed answer option distribution and question performance for the current scope.',
        notes_actions: 'Detailed Action Tracker notes, actions and document evidence coverage for the current scope.',
        target_remedy_dates: 'Detailed target remedy date status and overdue action performance for the current scope.'
      };

      return subtitles[this.selectedMetricDashboard] || 'Detailed dashboard for the current Action Tracker scope.';
    },

    drillThroughTitle() {
      if (this.selectedMetricDashboard) {
        return this.metricDashboardTitle;
      }

      if (this.selectedRegulatoryKey && this.selectedBusinessFunction) {
        return `${this.selectedRegulatoryKey} > ${this.selectedBusinessFunction}`;
      }

      if (this.selectedRegulatoryKey) {
        return `${this.selectedRegulatoryKey} Dashboard`;
      }

      return 'Overview Dashboard';
    },

    drillThroughSubtitle() {
      if (this.selectedMetricDashboard) {
        return this.metricDashboardSubtitle;
      }

      if (this.selectedRegulatoryKey && this.selectedBusinessFunction) {
        return `Detailed Action Tracker analytics for ${this.selectedBusinessFunction}.`;
      }

      if (this.selectedRegulatoryKey) {
        return `Drill-through analytics for ${this.selectedRegulatoryKey}.`;
      }

      return 'Overview across all filtered Action Tracker data.';
    },

    scopeInsight() {
      if (this.selectedMetricDashboard) {
        return 'This dashboard shows the selected right-column data set in more detail for the current Action Tracker scope.';
      }

      if (!this.isDrillThrough) {
        return 'This overview shows the full filtered Action Tracker data set. Click any Regulatory Key or Business Function to view granular drill-through analytics.';
      }

      if (this.selectedBusinessFunction) {
        return 'This drill-through view is scoped to one business function and focuses on answer distribution, remedy status, evidence, notes and the top actions to address.';
      }

      return 'This drill-through view is scoped to one Regulatory Key and shows the related business functions, performance gaps and action priorities.';
    },

    regulatoryPerformanceChartGroups() {
      return this.regulatoryKeys.map(item => {
        const modules = (item.modules || []).filter(module => module.score !== null && module.score !== undefined).slice().sort((a, b) => b.score - a.score);
        const best = modules[0] || { name: 'No data', score: 0 };
        const worst = modules.length ? modules[modules.length - 1] : { name: 'No data', score: 0 };

        return {
          regulatoryKey: item.name,
          bestFunction: best.name,
          bestScore: best.score,
          worstFunction: worst.name,
          worstScore: worst.score
        };
      });
    },

    diagnosticBarChartItems() {
      return [
        {
          label: 'Answered Questions',
          value: this.answeredCountForScope,
          class: 'answered',
          filterType: 'answered_questions'
        },
        {
          label: 'Actions Signed Off',
          value: this.completedCountForScope,
          class: 'completed',
          filterType: 'completed_questions'
        },
        {
          label: 'Correctly Answered',
          value: this.correctlyAnsweredCountForScope,
          class: 'positive',
          filterType: 'correctly_answered'
        },
        {
          label: 'Blank Responses',
          value: this.blankCountForScope,
          class: 'blank',
          filterType: 'blank_responses'
        },
        {
          label: 'Overdue Actions',
          value: this.overdueCountForScope,
          class: 'overdue',
          filterType: 'overdue'
        }
      ];
    },

    diagnosticBarChartMaxValue() {
      return Math.max(
          this.answeredCountForScope,
          this.completedCountForScope,
          this.correctlyAnsweredCountForScope,
          this.blankCountForScope,
          this.overdueCountForScope,
          1
      );
    },

    diagnosticSummaryCards() {
      return [
        {
          label: '% Actions signed off',
          value: `${this.completedRateForScope}%`,
          note: 'Status Completed',
          calculation: `${this.formatNumber(this.completedCountForScope)} / ${this.formatNumber(this.answeredCountForScope)}`,
          statusClass: this.completedRateForScope >= 65 ? 'status-good' : 'status-risk',
          filterType: 'completed_questions'
        },
        {
          label: '% Actions answered positively',
          value: `${this.accuracyRateForScope}%`,
          note: 'Positive / good outcomes',
          calculation: `${this.formatNumber(this.correctlyAnsweredCountForScope)} / ${this.formatNumber(this.answeredCountForScope)}`,
          statusClass: this.accuracyRateForScope >= 65 ? 'status-good' : 'status-warning',
          filterType: 'correctly_answered'
        },
        {
          label: '% of blank responses',
          value: `${this.blankRateForScope}%`,
          note: 'Data quality risk',
          calculation: `${this.formatNumber(this.blankCountForScope)} / ${this.formatNumber(this.answeredCountForScope)}`,
          statusClass: this.blankRateForScope > 5 ? 'status-warning' : 'status-good',
          filterType: 'blank_responses'
        },
        {
          label: 'Number of actions overdue',
          value: this.formatNumber(this.overdueCountForScope),
          note: 'Backlog risk',
          calculation: 'Target remedy < today',
          statusClass: this.overdueCountForScope > 0 ? 'status-risk' : 'status-good',
          filterType: 'overdue'
        }
      ];
    },

    overallPerformanceCards() {
      const bestKey = this.regulatoryKeys[0] || null;
      const worstKey = this.regulatoryKeys.length ? this.regulatoryKeys[this.regulatoryKeys.length - 1] : null;

      const allModules = this.regulatoryKeys
          .reduce((modules, key) => {
            return modules.concat((key.modules || []).map(module => {
              return Object.assign({}, module, {
                regulatoryKey: key.name
              });
            }));
          }, [])
          .filter(module => module.score !== null && module.score !== undefined)
          .sort((a, b) => b.score - a.score);

      const bestModule = allModules[0] || null;
      const worstModule = allModules.length ? allModules[allModules.length - 1] : null;

      return [
        {
          label: 'Best regulatory key',
          name: bestKey ? bestKey.name : 'No data',
          score: bestKey ? this.formatSectionScore(bestKey.score) : '—',
          statusClass: 'status-good',
          sourceType: 'regulatory_key',
          regulatoryKey: bestKey ? bestKey.name : null
        },
        {
          label: 'Area for improvement',
          name: worstKey ? worstKey.name : 'No data',
          score: worstKey ? this.formatSectionScore(worstKey.score) : '—',
          statusClass: 'status-risk',
          sourceType: 'regulatory_key',
          regulatoryKey: worstKey ? worstKey.name : null
        },
        {
          label: 'Best business function',
          name: bestModule ? bestModule.name : 'No data',
          score: bestModule ? this.formatSectionScore(bestModule.score) : '—',
          statusClass: 'status-good',
          sourceType: 'business_function',
          regulatoryKey: bestModule ? bestModule.regulatoryKey : null,
          businessFunction: bestModule ? bestModule.name : null
        },
        {
          label: 'Weakest business function',
          name: worstModule ? worstModule.name : 'No data',
          score: worstModule ? this.formatSectionScore(worstModule.score) : '—',
          statusClass: 'status-risk',
          sourceType: 'business_function',
          regulatoryKey: worstModule ? worstModule.regulatoryKey : null,
          businessFunction: worstModule ? worstModule.name : null
        }
      ];
    },

    regulatoryPerformanceActionPoints() {
      const improvementCards = this.overallPerformanceCards.filter(item => item.statusClass === 'status-risk' && item.name !== 'No data');
      const bestCards = this.overallPerformanceCards.filter(item => item.statusClass === 'status-good' && item.name !== 'No data');
      const points = [];

      if (improvementCards.length) {
        points.push(`Review ${improvementCards.map(item => item.name).join(' and ')} first, as these are the weakest areas in the current overview.`);
      }

      if (bestCards.length) {
        points.push(`Use ${bestCards.map(item => item.name).join(' and ')} as a benchmark for stronger operating practice.`);
      }

      if (this.overdueCountForScope > 0) {
        points.push(`${this.formatNumber(this.overdueCountForScope)} overdue actions need follow-up through Action Tracker.`);
      }

      points.push(`Compare best and worst business functions to identify whether the issue is execution consistency rather than knowledge.`);
      points.push(`Use the Regulatory Key overview dashboards below to drill through into the source data.`);

      return points.slice(0, 5);
    },

    answeredCountForScope() {
      return this.currentScopeAnswers.length;
    },

    completedCountForScope() {
      return this.currentScopeAnswers.filter(answer => this.isCompleted(answer)).length;
    },

    correctlyAnsweredCountForScope() {
      return this.currentScopeAnswers.filter(answer => this.isCorrectlyAnswered(answer)).length;
    },

    resolvedCountForScope() {
      return this.currentScopeAnswers.filter(answer => this.isResolved(answer)).length;
    },

    resolvedRateForScope() {
      return this.percent(this.resolvedCountForScope, this.answeredCountForScope);
    },

    awaitingSignOffActions() {
      return this.currentScopeAnswers
          .filter(answer => this.isAwaitingSignOff(answer))
          .map(answer => this.buildOutstandingAction(answer))
          .sort((a, b) => b.priority - a.priority);
    },

    blankCountForScope() {
      return this.currentScopeAnswers.filter(answer => this.isBlankAnswer(answer)).length;
    },

    overdueCountForScope() {
      return this.currentScopeAnswers.filter(answer => this.isOverdue(answer)).length;
    },

    overdueRagBreakdown() {
      return this.buildRagCounts(
          this.currentScopeAnswers.filter(answer => this.isOverdue(answer))
      );
    },

    targetedCountForScope() {
      return this.currentScopeAnswers.filter(answer => this.hasTargetRemedy(answer)).length;
    },

    outstandingCountForScope() {
      return this.currentScopeAnswers.filter(answer => !this.isResolved(answer)).length;
    },

    outstandingRagBreakdown() {
      return this.buildRagCounts(
          this.currentScopeAnswers.filter(answer => !this.isResolved(answer))
      );
    },

    documentsCountForScope() {
      return this.currentScopeAnswers.reduce((total, answer) => {
        return total + this.getDocumentsCount(answer);
      }, 0);
    },

    documentsByBusinessFunction() {
      const groups = {};

      this.currentScopeAnswers.forEach(answer => {
        const businessFunction = this.getBusinessFunction(answer);
        const regulatoryKey = this.getRegulatoryKey(answer);
        const key = `${regulatoryKey}|${businessFunction}`;

        if (!groups[key]) {
          groups[key] = {
            regulatoryKey,
            businessFunction,
            documents: 0
          };
        }

        groups[key].documents += this.getDocumentsCount(answer);
      });

      return Object.values(groups).sort((a, b) => b.documents - a.documents);
    },

    documentsByBusinessFunctionWithDocuments() {
      return this.documentsByBusinessFunction.filter(item => item.documents > 0);
    },

    completedRateForScope() {
      return this.percent(this.completedCountForScope, this.answeredCountForScope);
    },

    accuracyRateForScope() {
      return this.percent(this.correctlyAnsweredCountForScope, this.answeredCountForScope);
    },

    blankRateForScope() {
      return this.percent(this.blankCountForScope, this.answeredCountForScope, 2);
    },

    scopeSummaryCards() {
      return [
        {
          label: 'Signed off vs answered',
          value: `${this.completedRateForScope}%`,
          note: 'Status Completed',
          calculation: `${this.formatNumber(this.completedCountForScope)} / ${this.formatNumber(this.answeredCountForScope)}`,
          statusClass: this.completedRateForScope >= 65 ? 'status-good' : 'status-risk'
        },
        {
          label: 'Accuracy rate',
          value: `${this.accuracyRateForScope}%`,
          note: 'Correct / positive answers',
          calculation: `${this.formatNumber(this.correctlyAnsweredCountForScope)} / ${this.formatNumber(this.answeredCountForScope)}`,
          statusClass: this.accuracyRateForScope >= 65 ? 'status-good' : 'status-warning'
        },
        {
          label: 'Blank responses',
          value: `${this.blankRateForScope}%`,
          note: 'Data quality risk',
          calculation: `${this.formatNumber(this.blankCountForScope)} / ${this.formatNumber(this.answeredCountForScope)}`,
          statusClass: this.blankRateForScope > 5 ? 'status-warning' : 'status-good'
        },
        {
          label: 'Overdue items',
          value: this.formatNumber(this.overdueCountForScope),
          note: 'Backlog risk',
          calculation: 'Target remedy < today',
          statusClass: this.overdueCountForScope > 0 ? 'status-risk' : 'status-good'
        }
      ];
    },

    scopeKpiCards() {
      return [
        {
          label: 'Answered questions',
          value: this.formatNumber(this.answeredCountForScope),
          note: 'In selected scope'
        },
        {
          label: 'Actions resolved',
          value: `${this.resolvedRateForScope}%`,
          note: `${this.formatNumber(this.resolvedCountForScope)} positive or Completed`
        },
        {
          label: 'Actions signed off',
          value: `${this.completedRateForScope}%`,
          note: `${this.formatNumber(this.completedCountForScope)} with status Completed`
        },
        {
          label: 'Outstanding',
          value: this.formatNumber(this.outstandingCountForScope),
          note: 'Not resolved'
        },
        {
          label: 'Targeted',
          value: `${this.percent(this.targetedCountForScope, this.answeredCountForScope)}%`,
          note: `${this.formatNumber(this.targetedCountForScope)} with remedy date`
        },
        {
          label: 'Overdue',
          value: this.formatNumber(this.overdueCountForScope),
          note: 'Past remedy date'
        },
        {
          label: 'Firm notes',
          value: this.formatNumber(this.totalNotesForScope),
          note: 'Notes made'
        },
        {
          label: 'Documents',
          value: this.formatNumber(this.documentsCountForScope),
          note: 'Evidence added'
        }
      ];
    },

    regulatoryKeys() {
      if (this.enabledParentCategoryTree.length) {
        return this.buildEnabledParentCategoryPerformance();
      }

      return this.buildRegulatoryKeyPerformance(this.visibleAnswers);
    },

    scopedBusinessFunctionPerformance() {
      if (this.enabledParentCategoryTree.length) {
        return this.buildEnabledBusinessFunctionPerformance(
            this.selectedRegulatoryKey,
            this.selectedBusinessFunction
        );
      }

      return this.buildBusinessFunctionPerformance(this.currentScopeAnswers);
    },

    // True when a filter other than the Regulatory Key / Business Function
    // drill-through narrows the records (e.g. "With notes", "With documents").
    hasNarrowingFilter() {
      const ignoredKeys = ['parent', 'section', 'archived', 'sortColumn', 'sortDir', 'scrollY'];
      const filters = this.selectedFilters || {};

      // "Without archived" / "With archived" only widen the default scope;
      // "Only archived" (value 3) narrows it.
      if (filters.archived && Number(filters.archived.value) === 3) {
        return true;
      }

      return Object.keys(filters).some(key => {
        const filter = filters[key];

        // value 0 is the "All" option, i.e. no filter.
        return !ignoredKeys.includes(key)
            && filter
            && filter.value !== null
            && filter.value !== undefined
            && Number(filter.value) !== 0;
      });
    },

    // Without filters every enabled tile is shown (muted when it has no
    // records). With a narrowing filter only tiles that match it are shown.
    overviewRegulatoryKeys() {
      if (!this.hasNarrowingFilter) {
        return this.regulatoryKeys;
      }

      return this.regulatoryKeys
          .map(item => Object.assign({}, item, {
            modules: (item.modules || []).filter(module => this.hasRecordsInScope(module))
          }))
          .filter(item => item.modules.length);
    },

    visibleScopedBusinessFunctions() {
      if (!this.hasNarrowingFilter) {
        return this.scopedBusinessFunctionPerformance;
      }

      return this.scopedBusinessFunctionPerformance.filter(item => this.hasRecordsInScope(item));
    },

    answerOptionsForScope() {
      const total = this.currentScopeAnswers.length;
      const grouped = {};

      this.currentScopeAnswers.forEach(answer => {
        const label = this.getAnswerLabel(answer);

        if (!grouped[label]) {
          grouped[label] = {
            label,
            volume: 0
          };
        }

        grouped[label].volume += 1;
      });

      return Object.values(grouped).map(item => ({
        label: item.label,
        volume: item.volume,
        percent: this.percent(item.volume, total, 2),
        class: this.answerOptionClass(item.label)
      })).sort((a, b) => b.volume - a.volume);
    },

    answerOptionsRagBreakdown() {
      const counts = { red: 0, amber: 0, green: 0 };

      this.answerOptionsForScope.forEach(item => {
        if (item.class === 'risk') {
          counts.red += item.volume;
        } else if (item.class === 'warning') {
          counts.amber += item.volume;
        } else {
          counts.green += item.volume;
        }
      });

      return counts;
    },

    answerOptionsBarChartItems() {
      return this.answerOptionsForScope.map(item => ({
        label: item.label,
        value: item.volume,
        percent: item.percent,
        class: item.class,
        filterType: 'answer_option'
      }));
    },

    answerOptionsBarChartMaxValue() {
      return Math.max(
          ...this.answerOptionsBarChartItems.map(item => item.value),
          1
      );
    },

    notesActionsChartItems() {
      const withNotes = this.currentScopeAnswers.filter(answer => this.hasNotes(answer)).length;
      const withoutNotes = this.currentScopeAnswers.filter(answer => !this.hasNotes(answer)).length;

      return [
        {
          label: 'With notes',
          value: withNotes,
          class: 'notes',
          filterType: 'with_notes'
        },
        {
          label: 'Without notes',
          value: withoutNotes,
          class: 'no-notes',
          filterType: 'without_notes'
        },
        {
          label: 'Documents added',
          value: this.documentsCountForScope,
          class: 'documents',
          filterType: 'documents'
        },
        {
          label: 'Outstanding actions',
          value: this.outstandingCountForScope,
          class: 'outstanding',
          filterType: 'outstanding'
        }
      ];
    },

    notesActionsChartMaxValue() {
      return Math.max(
          ...this.notesActionsChartItems.map(item => item.value),
          1
      );
    },

    targetRemedyChartItems() {
      return this.remedyDatesForScope
          .filter(item => item.label !== 'Total')
          .map(item => ({
            label: item.label,
            value: item.value,
            percent: item.percent,
            class: this.targetRemedyChartClass(item.label),
            filterType: this.targetRemedyChartFilterType(item.label)
          }));
    },

    targetRemedyChartMaxValue() {
      return Math.max(
          ...this.targetRemedyChartItems.map(item => item.value),
          1
      );
    },

    questionsForScope() {
      return {
        answered: this.answeredCountForScope,
        completed: this.completedCountForScope,
        correctlyAnswered: this.correctlyAnsweredCountForScope,
        completedRate: this.completedRateForScope,
        accuracyRate: this.accuracyRateForScope,
        insight: `${this.completedRateForScope}% actions signed off and ${this.accuracyRateForScope}% actions answered positively. Accuracy rate is the share of selected actions assessed as a positive or good outcome.`
      };
    },

    totalNotesForScope() {
      return this.currentScopeAnswers.reduce((total, answer) => {
        return total + this.getNotesCount(answer);
      }, 0);
    },

    notesForScope() {
      const withNotesCompleted = this.currentScopeAnswers.filter(answer => this.hasNotes(answer) && this.isResolved(answer)).length;
      const withNotesNotCompleted = this.currentScopeAnswers.filter(answer => this.hasNotes(answer) && !this.isResolved(answer)).length;
      const withoutNotesCompleted = this.currentScopeAnswers.filter(answer => !this.hasNotes(answer) && this.isResolved(answer)).length;
      const withoutNotesNotCompleted = this.currentScopeAnswers.filter(answer => !this.hasNotes(answer) && !this.isResolved(answer)).length;

      return [
        {
          label: 'Total notes',
          value: this.totalNotesForScope
        },
        {
          label: 'Questions with notes resolved',
          value: withNotesCompleted
        },
        {
          label: 'Questions with notes not resolved',
          value: withNotesNotCompleted
        },
        {
          label: 'Questions without notes resolved',
          value: withoutNotesCompleted
        },
        {
          label: 'Questions without notes not resolved',
          value: withoutNotesNotCompleted
        }
      ];
    },

    remedyDatesForScope() {
      return [
        {
          label: 'Total',
          value: this.answeredCountForScope,
          percent: null
        },
        {
          label: 'Targeted',
          value: this.targetedCountForScope,
          percent: this.percent(this.targetedCountForScope, this.answeredCountForScope)
        },
        {
          label: 'Resolved',
          value: this.resolvedCountForScope,
          percent: this.resolvedRateForScope
        },
        {
          label: 'Outstanding',
          value: this.outstandingCountForScope,
          percent: this.percent(this.outstandingCountForScope, this.answeredCountForScope)
        },
        {
          label: 'Overdue',
          value: this.overdueCountForScope,
          percent: this.percent(this.overdueCountForScope, this.answeredCountForScope)
        }
      ];
    },

    remedyDatesInsight() {
      return `${this.percent(this.targetedCountForScope, this.answeredCountForScope)}% of selected items have a defined remedy date, while ${this.formatNumber(this.overdueCountForScope)} items are overdue.`;
    },

    overviewBestWorstCards() {
      const bestKey = this.regulatoryKeys[0] || null;
      const worstKey = this.regulatoryKeys.length ? this.regulatoryKeys[this.regulatoryKeys.length - 1] : null;

      const allModules = this.regulatoryKeys
          .reduce((modules, key) => modules.concat(key.modules || []), [])
          .sort((a, b) => b.score - a.score);

      const bestModule = allModules[0] || null;
      const worstModule = allModules.length ? allModules[allModules.length - 1] : null;

      return [
        {
          label: 'Best regulatory key',
          name: bestKey ? bestKey.name : 'No data',
          score: bestKey ? this.formatSectionScore(bestKey.score) : '—'
        },
        {
          label: 'Worst regulatory key',
          name: worstKey ? worstKey.name : 'No data',
          score: worstKey ? this.formatSectionScore(worstKey.score) : '—'
        },
        {
          label: 'Best business function',
          name: bestModule ? bestModule.name : 'No data',
          score: bestModule ? this.formatSectionScore(bestModule.score) : '—'
        },
        {
          label: 'Worst business function',
          name: worstModule ? worstModule.name : 'No data',
          score: worstModule ? this.formatSectionScore(worstModule.score) : '—'
        }
      ];
    },

    bestWorstCards() {
      if (this.selectedBusinessFunction) {
        const bestQuestion = this.topQuestions[0] || null;
        const worstQuestion = this.bottomQuestions[0] || null;

        return [
          {
            label: 'Current function score',
            name: this.selectedBusinessFunction,
            score: `${this.accuracyRateForScope}%`
          },
          {
            label: 'Best question',
            name: bestQuestion ? bestQuestion.question : 'No data',
            score: bestQuestion ? `${bestQuestion.score}%` : '-'
          },
          {
            label: 'Worst question',
            name: worstQuestion ? worstQuestion.question : 'No data',
            score: worstQuestion ? `${worstQuestion.score}%` : '-'
          }
        ];
      }

      const sorted = this.scopedBusinessFunctionPerformance.slice().sort((a, b) => b.score - a.score);
      const best = sorted[0] || null;
      const worst = sorted.length ? sorted[sorted.length - 1] : null;

      return [
        {
          label: 'Best business function',
          name: best ? best.name : 'No data',
          score: best ? `${best.score}%` : '-'
        },
        {
          label: 'Worst business function',
          name: worst ? worst.name : 'No data',
          score: worst ? `${worst.score}%` : '-'
        }
      ];
    },

    questionPerformanceForScope() {
      const grouped = {};

      this.currentScopeAnswers.forEach(answer => {
        const partialScore = this.getPartialScore(answer);

        if (partialScore === null) {
          return;
        }

        const question = this.getQuestionText(answer);

        if (!grouped[question]) {
          grouped[question] = {
            question,
            total: 0,
            scoreSum: 0
          };
        }

        grouped[question].total += 1;
        grouped[question].scoreSum += partialScore;
      });

      return Object.values(grouped).map(item => ({
        question: item.question,
        // Kept to 1 decimal place (not rounded to a whole number) so
        // two questions with genuinely different averages never show
        // as an identical percentage purely due to rounding.
        score: item.total ? Math.round((item.scoreSum / item.total) * 10) / 10 : 0,
        total: item.total
      }));
    },

    questionPerformanceSplit() {
      const sortedDescending = this.questionPerformanceForScope
          .slice()
          .sort((a, b) => b.score - a.score);

      const total = sortedDescending.length;

      // Split into two disjoint groups so the same question can never
      // appear in both "highest" and "lowest" — when there are fewer
      // than 10 unique questions, a plain top-5/bottom-5 would overlap.
      const highestCount = Math.min(5, Math.ceil(total / 2));
      const lowestCount = Math.min(5, total - highestCount);

      return {
        highest: sortedDescending.slice(0, highestCount),
        lowest: sortedDescending.slice(total - lowestCount).reverse()
      };
    },

    topQuestions() {
      return this.questionPerformanceSplit.highest;
    },

    bottomQuestions() {
      return this.questionPerformanceSplit.lowest;
    },

    // Same ranking as Top Dynamic Steers; sent to the AI so action points
    // can link to these exact actions.
    topOutstandingActions() {
      return this.topDynamicSteers;
    },

    // Business functions ranked by the total priority of their unresolved
    // actions (same scoring as Top Dynamic Steers): the overview and
    // regulatory key AI action points cover these, highest risk first.
    topRiskAreas() {
      const areas = {};

      this.currentScopeAnswers
          .filter(answer => !this.isResolved(answer))
          .forEach(answer => {
            const action = this.buildOutstandingAction(answer);
            const key = `${action.regulatoryKey}|${action.businessFunction}`;

            if (!areas[key]) {
              areas[key] = {
                regulatoryKey: action.regulatoryKey,
                businessFunction: action.businessFunction,
                riskScore: 0,
                outstanding: 0,
                overdue: 0,
                highSeverity: 0,
                topAction: null
              };
            }

            const area = areas[key];

            area.riskScore += action.priority;
            area.outstanding += 1;
            area.overdue += action.overdue ? 1 : 0;
            area.highSeverity += /high|red/i.test(action.severity) ? 1 : 0;

            if (!area.topAction || action.priority > area.topAction.priority) {
              area.topAction = action;
            }
          });

      return Object.values(areas)
          .sort((a, b) => b.riskScore - a.riskScore)
          .slice(0, 5)
          .map(area => ({
            regulatoryKey: area.regulatoryKey,
            businessFunction: area.businessFunction,
            riskScore: area.riskScore,
            outstanding: area.outstanding,
            overdue: area.overdue,
            highSeverity: area.highSeverity,
            topAction: area.topAction.question,
            topActionAnswer: area.topAction.answer
          }));
    },

    aiGeneratedLabel() {
      const generatedAt = this.aiActionPoints && this.aiActionPoints.generatedAt;
      const date = generatedAt ? new Date(generatedAt) : null;

      if (!date || isNaN(date.getTime())) {
        return null;
      }

      const formatted = date.toLocaleString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });

      return this.aiActionPoints.cached
          ? `Generated ${formatted}. The data has not changed since — use Regenerate for a fresh analysis.`
          : `Generated ${formatted}.`;
    },

    aiActionPointsEmptyText() {
      if (!this.currentScopeAnswers.length) {
        return 'No Action Tracker records match the current filters for this area, so there is nothing to analyse.';
      }

      return 'No AI action points generated for this data set.';
    },

    topDynamicSteers() {
      return this.currentScopeAnswers
          .filter(answer => !this.isResolved(answer))
          .map(answer => this.buildOutstandingAction(answer))
          .sort((a, b) => b.priority - a.priority)
          .slice(0, 5);
    }
  },

  methods: {
    handleHeaderBackClick() {
      if (this.selectedMetricDashboard) {
        this.clearMetricDashboard();
        return;
      }

      this.clearDrillThrough();
    },

    openMetricDashboard(type) {
      this.setMetricDashboard(type);
    },

    clearMetricDashboard() {
      this.setMetricDashboard(null);
    },

    targetRemedyChartClass(label) {
      const normalisedLabel = String(label || '').toLowerCase();

      if (normalisedLabel.includes('overdue')) {
        return 'overdue';
      }

      if (normalisedLabel.includes('outstanding')) {
        return 'outstanding';
      }

      if (normalisedLabel.includes('completed') || normalisedLabel.includes('resolved')) {
        return 'completed';
      }

      return 'targeted';
    },

    targetRemedyChartFilterType(label) {
      const normalisedLabel = String(label || '').toLowerCase();

      if (normalisedLabel.includes('overdue')) {
        return 'overdue';
      }

      if (normalisedLabel.includes('outstanding')) {
        return 'outstanding';
      }

      if (normalisedLabel.includes('completed') || normalisedLabel.includes('resolved')) {
        return 'completed_questions';
      }

      return 'target_remedy_dates';
    },

    // force: Regenerate button — ignore the stored analysis and ask OpenAI again.
    requestAiActionPoints(force = false) {
      // No records in scope (e.g. filters exclude this area): GPT would only
      // invent generic advice from zeros, so don't call it at all.
      if (!this.hasData || !this.currentScopeAnswers.length) {
        this.aiActionPoints = { weaknesses: [], strengths: [], items: [] };
        this.isLoadingAiActionPoints = false;
        this.aiActionPointsError = null;
        return;
      }

      const requestId = ++this.aiActionRequestId;

      this.clearAiActionPointsTimer();

      this.isLoadingAiActionPoints = true;
      this.aiActionPointsError = null;
      this.aiActionPoints = { weaknesses: [], strengths: [], items: [] };

      const payload = Object.assign(this.buildAiActionPointsPayload(), { force: force === true });

      this.callAiActionPointsBackend(payload)
          .then(points => {
            if (requestId !== this.aiActionRequestId) {
              return;
            }

            this.aiActionPoints = points;
            this.isLoadingAiActionPoints = false;
          })
          .catch(() => {
            if (requestId !== this.aiActionRequestId) {
              return;
            }

            this.aiActionPoints = { weaknesses: [], strengths: [], items: [] };
            this.aiActionPointsError = 'Unable to generate AI action points. Please try again.';
            this.isLoadingAiActionPoints = false;
          });
    },

    callAiActionPointsBackend(payload) {
      if (!AI_ACTION_POINTS_ENDPOINT) {
        return this.fakeBackendAiActionPointsRequest(payload);
      }

      if (!window.axios) {
        return Promise.reject(new Error('Axios is not available.'));
      }

      return window.axios
          .post(AI_ACTION_POINTS_ENDPOINT, payload)
          .then(response => this.normaliseAiActionPointsResponse(response.data));
    },

    normaliseAiActionPointsResponse(data) {
      const meta = (data && data.meta) || {};

      return {
        weaknesses: this.normaliseAiPointGroup(data && data.weaknesses),
        strengths: this.normaliseAiPointGroup(data && data.strengths),
        items: this.normaliseAiPointGroup(data && data.action_points),
        cached: meta.cached === true,
        generatedAt: meta.generated_at || null
      };
    },

    normaliseAiPointGroup(points) {
      if (!Array.isArray(points)) {
        return [];
      }

      return points.map(point => ({
        title: point.title || point.heading || 'AI action point',
        description: point.description || point.text || point.summary || '',
        sourceLabel: point.sourceLabel || point.source_label || point.link_label || null,
        sourceType: point.sourceType || point.source_type || null,
        regulatoryKey: point.regulatoryKey || point.regulatory_key || null,
        businessFunction: point.businessFunction || point.business_function || null,
        filterType: point.filterType || point.filter_type || null,
        actionId: point.actionId || point.action_id || null
      })).filter(point => point.description || point.title);
    },

    fakeBackendAiActionPointsRequest(payload) {
      const delay = 2500 + Math.floor(Math.random() * 1500);

      return new Promise(resolve => {
        this.aiActionPointsTimer = window.setTimeout(() => {
          resolve(this.buildMockAiActionPointsResponse(payload));
        }, delay);
      });
    },

    buildAiActionPointsPayload() {
      return {
        dashboard: this.aiDashboardKind,

        scope: {
          regulatoryKey: this.selectedRegulatoryKey,
          businessFunction: this.selectedBusinessFunction,
          title: this.drillThroughTitle
        },

        businessFunctionsInScope: this.buildBusinessFunctionsInScopeForAi(),

        metrics: {
          answered: this.answeredCountForScope,
          completed: this.completedCountForScope,
          completedRate: this.completedRateForScope,
          resolved: this.resolvedCountForScope,
          resolvedRate: this.resolvedRateForScope,
          awaitingSignOff: this.awaitingSignOffActions.length,
          correctlyAnswered: this.correctlyAnsweredCountForScope,
          accuracyRate: this.accuracyRateForScope,
          blankResponses: this.blankCountForScope,
          blankRate: this.blankRateForScope,
          targeted: this.targetedCountForScope,
          overdue: this.overdueCountForScope,
          outstanding: this.outstandingCountForScope,
          notes: this.totalNotesForScope,
          documents: this.documentsCountForScope
        },

        weakestBusinessFunctions: this.buildWeakestBusinessFunctionsForAi(),

        topOutstandingActions: this.topOutstandingActions,

        topRiskAreas: this.topRiskAreas,

        awaitingSignOffActions: this.awaitingSignOffActions.slice(0, 5).map(item => ({
          questionId: item.questionId,
          regulatoryKey: item.regulatoryKey,
          businessFunction: item.businessFunction,
          question: item.question,
          answer: item.answer
        })),

        overdueRag: [
          { rating: 'Red', count: this.overdueRagBreakdown.red },
          { rating: 'Amber', count: this.overdueRagBreakdown.amber },
          { rating: 'Green', count: this.overdueRagBreakdown.green }
        ],

        answerOptions: this.answerOptionsForScope.slice(0, 5),

        answerOptionsRag: [
          { rating: 'Red', count: this.answerOptionsRagBreakdown.red },
          { rating: 'Amber', count: this.answerOptionsRagBreakdown.amber },
          { rating: 'Green', count: this.answerOptionsRagBreakdown.green }
        ],

        bestWorst: this.bestWorstCards,

        documentsByBusinessFunction: this.documentsByBusinessFunction.slice(0, 10),

        outstandingRag: [
          { rating: 'Red', count: this.outstandingRagBreakdown.red },
          { rating: 'Amber', count: this.outstandingRagBreakdown.amber },
          { rating: 'Green', count: this.outstandingRagBreakdown.green }
        ],

        overallPerformance: this.overallPerformanceCards,

        dashboardData: {
          diagnostic: this.diagnosticBarChartItems,
          answerOptions: this.answerOptionsBarChartItems,
          notesActions: this.notesActionsChartItems,
          targetRemedyDates: this.targetRemedyChartItems
        }
      };
    },

    buildBusinessFunctionsInScopeForAi() {
      if (this.selectedBusinessFunction) {
        return [];
      }

      if (this.selectedRegulatoryKey) {
        return this.scopedBusinessFunctionPerformance
            .filter(item => this.hasRecordsInScope(item))
            .map(item => ({
              name: item.name,
              score: item.score,
              regulatoryKey: this.selectedRegulatoryKey
            }));
      }

      return this.regulatoryKeys.reduce((functions, key) => {
        return functions.concat((key.modules || [])
            .filter(module => this.hasRecordsInScope(module))
            .map(module => ({
              name: module.name,
              score: module.score,
              regulatoryKey: key.name
            })));
      }, []);
    },

    // Tiles are always shown now, but the AI should only point at business
    // functions that have Action Tracker records in the current filters.
    hasRecordsInScope(item) {
      return item.records === null || item.records === undefined || item.records > 0;
    },

    buildWeakestBusinessFunctionsForAi() {
      if (this.selectedRegulatoryKey) {
        return this.scopedBusinessFunctionPerformance
            .filter(item => this.hasRecordsInScope(item))
            .sort((a, b) => a.score - b.score)
            .slice(0, 3)
            .map(item => Object.assign({}, item, {
              regulatoryKey: this.selectedRegulatoryKey
            }));
      }

      return this.regulatoryKeys
          .reduce((modules, key) => {
            return modules.concat((key.modules || []).filter(module => this.hasRecordsInScope(module)).map(module => {
              return Object.assign({}, module, {
                regulatoryKey: key.name
              });
            }));
          }, [])
          .sort((a, b) => a.score - b.score)
          .slice(0, 3);
    },

    buildMockAiActionPointsResponse(payload) {
      const weaknesses = [];
      const strengths = [];
      const metrics = payload.metrics;
      const weakestBusinessFunctions = payload.weakestBusinessFunctions || [];
      const topOutstandingActions = payload.topOutstandingActions || [];

      if (weakestBusinessFunctions.length) {
        const weakest = weakestBusinessFunctions[0];

        weaknesses.push({
          title: 'Focus on weakest business functions',
          description: `${weakestBusinessFunctions.map(item => item.name).join(', ')} show the weakest performance in ${payload.scope.title} and should be reviewed first.`,
          sourceLabel: 'Open weakest business function dashboard',
          sourceType: 'business_function',
          regulatoryKey: weakest.regulatoryKey || payload.scope.regulatoryKey,
          businessFunction: weakest.name
        });
      }

      if (metrics.overdue > 0) {
        weaknesses.push({
          title: 'Prioritise overdue remediation',
          description: `${this.formatNumber(metrics.overdue)} items are overdue. These should be reviewed before lower-risk outstanding actions.`,
          sourceLabel: 'Open overdue Action Tracker items',
          sourceType: 'action_tracker_filter',
          filterType: 'overdue'
        });
      } else {
        strengths.push({
          title: 'No overdue remediation',
          description: 'No items are currently overdue in the selected data, showing remediation is being kept on schedule.',
          sourceLabel: 'Open target remedy date data',
          sourceType: 'action_tracker_filter',
          filterType: 'target_remedy_dates'
        });
      }

      if (metrics.targeted < metrics.answered) {
        weaknesses.push({
          title: 'Improve target remedy discipline',
          description: `${this.formatNumber(metrics.targeted)} of ${this.formatNumber(metrics.answered)} items have a target remedy date. Missing dates reduce remediation control.`,
          sourceLabel: 'Open target remedy date data',
          sourceType: 'action_tracker_filter',
          filterType: 'target_remedy_dates'
        });
      } else {
        strengths.push({
          title: 'Strong target remedy discipline',
          description: `All ${this.formatNumber(metrics.answered)} answered items have a target remedy date, giving full remediation control.`,
          sourceLabel: 'Open target remedy date data',
          sourceType: 'action_tracker_filter',
          filterType: 'target_remedy_dates'
        });
      }

      if (metrics.documents < metrics.completed) {
        weaknesses.push({
          title: 'Strengthen evidence capture',
          description: `${this.formatNumber(metrics.documents)} documents are attached against ${this.formatNumber(metrics.completed)} completed items. Completed answers should be supported by evidence.`,
          sourceLabel: 'Open notes and documents data',
          sourceType: 'action_tracker_filter',
          filterType: 'notes_and_documents'
        });
      } else {
        strengths.push({
          title: 'Evidence capture keeps pace with completion',
          description: `${this.formatNumber(metrics.documents)} documents are attached against ${this.formatNumber(metrics.completed)} completed items.`,
          sourceLabel: 'Open notes and documents data',
          sourceType: 'action_tracker_filter',
          filterType: 'notes_and_documents'
        });
      }

      if (metrics.blankResponses > 0) {
        weaknesses.push({
          title: 'Reduce blank responses',
          description: `Blank responses represent ${metrics.blankRate}% of selected answers and should be treated as a data quality issue.`,
          sourceLabel: 'Open blank response data',
          sourceType: 'action_tracker_filter',
          filterType: 'blank_responses'
        });
      } else {
        strengths.push({
          title: 'No blank responses',
          description: 'No blank responses were found in the selected data, indicating good data completeness.',
          sourceLabel: 'Open blank response data',
          sourceType: 'action_tracker_filter',
          filterType: 'blank_responses'
        });
      }

      if (topOutstandingActions.length) {
        const topAction = topOutstandingActions[0];

        weaknesses.push({
          title: 'Review the highest priority outstanding actions',
          description: `Start with "${topAction.question}" because it has the highest priority score in the selected data.`,
          sourceLabel: 'Open highest priority action data',
          sourceType: 'business_function',
          regulatoryKey: topAction.regulatoryKey,
          businessFunction: topAction.businessFunction
        });
      }

      if (metrics.accuracyRate >= 65) {
        strengths.push({
          title: 'Healthy positive-answer rate',
          description: `${metrics.accuracyRate}% of selected items are answered positively or assessed as a good outcome.`,
          sourceLabel: 'Open diagnostic question data',
          sourceType: 'current_scope'
        });
      }

      return {
        weaknesses: this.ensureThreeAiActionPoints(weaknesses, payload, 'weakness'),
        strengths: this.ensureThreeAiActionPoints(strengths, payload, 'strength')
      };
    },

    ensureThreeAiActionPoints(points, payload, kind) {
      const metrics = payload.metrics;

      const fallbackPoints = kind === 'weakness' ? [
        {
          title: 'Review answer option trends',
          description: `Review the most common answer options in ${payload.scope.title} to identify repeated weak responses and recurring compliance gaps.`,
          sourceLabel: 'Open answer option performance',
          sourceType: 'current_scope'
        },
        {
          title: 'Validate completion quality',
          description: `${metrics.completedRate}% of selected items are completed, while ${metrics.accuracyRate}% are answered positively. Review the gap between completion and quality.`,
          sourceLabel: 'Open diagnostic question data',
          sourceType: 'current_scope'
        },
        {
          title: 'Monitor progress through the next review cycle',
          description: `Track outstanding items in ${payload.scope.title} and compare progress after the next Action Tracker update.`,
          sourceLabel: 'Open outstanding Action Tracker items',
          sourceType: 'action_tracker_filter',
          filterType: 'outstanding'
        }
      ] : [
        {
          title: 'Consistent completion tracking',
          description: `${this.formatNumber(metrics.completed)} of ${this.formatNumber(metrics.answered)} items are completed in ${payload.scope.title}.`,
          sourceLabel: 'Open diagnostic question data',
          sourceType: 'current_scope'
        },
        {
          title: 'Notes and evidence recorded',
          description: `${this.formatNumber(metrics.notes)} notes and ${this.formatNumber(metrics.documents)} documents are recorded in this scope.`,
          sourceLabel: 'Open notes and documents data',
          sourceType: 'action_tracker_filter',
          filterType: 'notes_and_documents'
        },
        {
          title: 'Dynamic steers kept up to date',
          description: 'The Top Dynamic Steers section reflects the current short-term priority list for the selected regulatory area or business function.',
          sourceLabel: 'Open dynamic steers',
          sourceType: 'current_scope'
        }
      ];

      fallbackPoints.forEach(point => {
        if (points.length >= 3) {
          return;
        }

        const alreadyExists = points.some(existingPoint => existingPoint.title === point.title);

        if (!alreadyExists) {
          points.push(point);
        }
      });

      return points.slice(0, 3);
    },

    handleAiActionPointSource(action) {
      if (action.sourceType === 'action' && action.actionId) {
        this.openAction({ questionId: action.actionId });
        return;
      }

      if (action.sourceType === 'business_function' && action.regulatoryKey && action.businessFunction) {
        this.selectBusinessFunction(action.regulatoryKey, action.businessFunction);
        return;
      }

      if (action.sourceType === 'regulatory_key' && action.regulatoryKey) {
        this.selectRegulatoryKey(action.regulatoryKey);
        return;
      }

      if (action.regulatoryKey) {
        this.selectRegulatoryKey(action.regulatoryKey);
      }
    },

    handlePerformanceCardClick(item) {
      if (!item) {
        return;
      }

      if (item.sourceType === 'business_function' && item.regulatoryKey && item.businessFunction) {
        this.selectBusinessFunction(item.regulatoryKey, item.businessFunction);
        return;
      }

      if (item.sourceType === 'regulatory_key' && item.regulatoryKey) {
        this.selectRegulatoryKey(item.regulatoryKey);
      }
    },

    clearAiActionPointsTimer() {
      if (this.aiActionPointsTimer) {
        window.clearTimeout(this.aiActionPointsTimer);
        this.aiActionPointsTimer = null;
      }
    },

    selectRegulatoryKey(name) {
      this.setDrillThrough(name, null);
    },

    selectBusinessFunction(regulatoryKey, businessFunction) {
      this.setDrillThrough(regulatoryKey, businessFunction);
    },

    clearDrillThrough() {
      this.setDrillThrough(null, null);
    },

    // Drill-through clicked inside the dashboard: update it here and ask the
    // Action Tracker to set the matching filters, so the filter bar, the table
    // and the dashboard always show the same scope.
    setDrillThrough(regulatoryKey, businessFunction) {
      const filterNames = this.drillToFilterNames(regulatoryKey, businessFunction);

      this.setMetricDashboard(null);
      this.selectedRegulatoryKey = regulatoryKey || null;
      this.selectedBusinessFunction = businessFunction || null;

      // The filter change comes back through filterDrillSignature; skip it
      // once so a name that has no filter option can't reset the drill-through.
      this.ignoreNextFilterDrillChange = true;
      this.$emit('drill-change', filterNames);
      this.$nextTick(() => {
        this.ignoreNextFilterDrillChange = false;
      });
    },

    // Filter bar -> dashboard. Filter options use the raw answer.parent /
    // answer.section titles; the dashboard uses enabled category titles, so
    // map through a matching answer when there is one.
    applyFiltersToDrill() {
      if (this.ignoreNextFilterDrillChange) {
        this.ignoreNextFilterDrillChange = false;
        return;
      }

      const parentName = this.activeFilterName('parent');
      const sectionName = this.activeFilterName('section');

      if (!parentName && !sectionName) {
        this.selectedRegulatoryKey = null;
        this.selectedBusinessFunction = null;
        return;
      }

      const match = this.visibleAnswers.find(answer => {
        return (!parentName || this.sameText(answer.parent, parentName))
            && (!sectionName || this.sameText(answer.section, sectionName));
      });

      this.selectedRegulatoryKey = match ? this.getRegulatoryKey(match) : parentName;
      this.selectedBusinessFunction = sectionName
          ? (match ? this.getBusinessFunction(match) : sectionName)
          : null;
    },

    drillToFilterNames(regulatoryKey, businessFunction) {
      const match = this.visibleAnswers.find(answer => {
        return (!regulatoryKey || this.getRegulatoryKey(answer) === regulatoryKey)
            && (!businessFunction || this.getBusinessFunction(answer) === businessFunction);
      });

      return {
        parent: regulatoryKey ? this.normaliseText(match ? match.parent : regulatoryKey) : null,
        section: businessFunction ? this.normaliseText(match ? match.section : businessFunction) : null
      };
    },

    activeFilterName(key) {
      const filter = this.selectedFilters && this.selectedFilters[key];

      // value 0 is the "All" option, i.e. no filter.
      if (!filter || filter.value === null || filter.value === undefined || Number(filter.value) === 0) {
        return null;
      }

      return this.normaliseText(filter.name) || null;
    },

    // Opens the action in the Action Tracker; its Back button returns here.
    openAction(item) {
      if (item && item.questionId) {
        this.$emit('open-action', item.questionId);
      }
    },

    setMetricDashboard(type) {
      if (this.selectedMetricDashboard === type) {
        return;
      }

      this.selectedMetricDashboard = type;
      this.$emit('metric-dashboard-change', type);
    },

    normaliseEnabledParentCategories(collection) {
      const categories = [];
      let parsedCollection = collection;

      // Blade may pass the prop as a JSON string when the binding does not use `:`.
      // Parse it here so the component still reads the exact backend values.
      if (typeof parsedCollection === 'string') {
        parsedCollection = this.parseEnabledParentCategoriesJson(parsedCollection);
      }

      const source = Array.isArray(parsedCollection)
          ? parsedCollection
          : (parsedCollection && typeof parsedCollection === 'object'
              ? Object.values(parsedCollection)
              : []);

      source.forEach(parent => {
        const normalisedParent = this.normaliseEnabledParentCategory(parent);

        if (normalisedParent) {
          categories.push(normalisedParent);
        }
      });

      return categories.sort((a, b) => a.order - b.order);
    },

    parseEnabledParentCategoriesJson(value) {
      let parsed = value;

      // Supports both normal JSON and accidentally double-encoded JSON.
      for (let attempt = 0; attempt < 2 && typeof parsed === 'string'; attempt += 1) {
        const candidate = parsed.trim();

        if (!candidate) {
          return [];
        }

        try {
          parsed = JSON.parse(candidate);
        } catch (error) {
          console.error('Unable to parse enabledparentcategories.', error);
          return [];
        }
      }

      return parsed;
    },

    normaliseEnabledParentCategory(parent) {
      if (!parent) {
        return null;
      }

      const children = Array.isArray(parent.children)
          ? parent.children
          : Object.values(parent.children || {});

      const normalisedParent = {
        id: parent.id || null,
        parentId: parent.parent_id || parent.parentId || null,
        key: this.normaliseText(parent.key || ''),
        title: this.normaliseText(parent.title || parent.name || parent.label || ''),
        subtitle: this.normaliseText(parent.subtitle || ''),
        text: this.normaliseText(parent.text || ''),
        dashboardContent: parent.dashboard_content || parent.dashboardContent || '',
        order: Number(parent.order || 0),
        score: this.extractEnabledSectionScore(parent),
        countQuestion: this.extractEnabledQuestionCount(parent),
        raw: parent,
        children: []
      };

      normalisedParent.children = children
          .map(child => this.normaliseEnabledBusinessFunction(child, normalisedParent))
          .filter(Boolean)
          .sort((a, b) => a.order - b.order);

      if (!normalisedParent.title && !normalisedParent.key && !normalisedParent.children.length) {
        return null;
      }

      return normalisedParent;
    },

    normaliseEnabledBusinessFunction(section, parent) {
      if (!section) {
        return null;
      }

      return {
        id: section.id || section.section_id || section.question_section_id || null,
        parentId: section.parent_id || section.parentId || (parent && parent.id) || null,
        parentKey: parent ? parent.key : this.normaliseText(section.parent_key || section.parentKey || ''),
        parentTitle: parent ? parent.title : this.normaliseText(section.parent_title || section.parentTitle || ''),
        key: this.normaliseText(section.key || section.section_key || section.sectionKey || ''),
        title: this.normaliseText(section.title || section.name || section.section || section.label || ''),
        subtitle: this.normaliseText(section.subtitle || ''),
        text: this.normaliseText(section.text || ''),
        dashboardContent: section.dashboard_content || section.dashboardContent || '',
        order: Number(section.order || 0),
        score: this.extractEnabledSectionScore(section),
        countQuestion: this.extractEnabledQuestionCount(section),
        raw: section
      };
    },

    extractEnabledSectionScore(section) {
      if (!section) {
        return null;
      }

      // Read the score directly from the prop. The only transformation is
      // converting the stored fraction (for example 0.63) to display percent (63).
      const rawScore = section.last_score && section.last_score.section_percent !== undefined
          ? section.last_score.section_percent
          : (section.lastScore && section.lastScore.section_percent !== undefined
              ? section.lastScore.section_percent
              : (section.section_percent !== undefined ? section.section_percent : null));

      return this.formatDirectSectionPercent(rawScore);
    },

    extractEnabledQuestionCount(section) {
      if (!section) {
        return null;
      }

      const fields = [
        'count_question',
        'question_count',
        'questions_count',
        'countQuestion',
        'questionCount',
        'total_questions'
      ];

      const field = fields.find(name => section[name] !== null && section[name] !== undefined && section[name] !== '');

      return field ? Number(section[field]) : null;
    },

    formatDirectSectionPercent(value) {
      if (value === null || value === undefined || value === '') {
        return null;
      }

      const numericValue = Number(value);

      if (Number.isNaN(numericValue)) {
        return null;
      }

      return Math.round(numericValue * 100);
    },

    buildEnabledParentCategoryPerformance() {
      // enabledparentcategories is the source of truth for Regulatory Keys and Business Functions.
      // Do not derive or replace its scores/counts from Action Tracker answers.
      return this.enabledParentCategoryTree.map(parent => ({
        name: parent.title || parent.key || 'Unassigned Regulatory Key',
        key: parent.key,
        id: parent.id,
        description: parent.subtitle || parent.text || '',
        score: parent.score,
        total: parent.countQuestion,
        dashboardContent: parent.dashboardContent,
        modules: (parent.children || []).map(child => ({
          name: child.title || child.key || 'Unassigned Business Function',
          key: child.key,
          id: child.id,
          score: child.score,
          total: child.countQuestion,
          countQuestion: child.countQuestion,
          records: this.recordsForSection(child),
          dashboardContent: child.dashboardContent,
          parentName: parent.title,
          parentKey: parent.key
        }))
      }));
    },

    buildEnabledBusinessFunctionPerformance(regulatoryKey = null, businessFunction = null) {
      let sections = this.enabledBusinessFunctionSections;

      if (regulatoryKey) {
        sections = sections.filter(section => {
          return this.sameText(section.parentTitle, regulatoryKey)
              || this.sameText(section.parentKey, regulatoryKey);
        });
      }

      if (businessFunction) {
        sections = sections.filter(section => {
          return this.sameText(section.title, businessFunction)
              || this.sameText(section.key, businessFunction);
        });
      }

      // Keep the values and ordering supplied by enabledparentcategories.
      return sections.map(section => ({
        name: section.title || section.key || 'Unassigned Business Function',
        key: section.key,
        id: section.id,
        total: section.countQuestion,
        countQuestion: section.countQuestion,
        score: section.score,
        records: this.recordsForSection(section),
        regulatoryKey: section.parentTitle,
        regulatoryKeyKey: section.parentKey,
        dashboardContent: section.dashboardContent
      }));
    },

    recordsForSection(section) {
      if (!section || section.id === null || section.id === undefined) {
        return null;
      }

      return this.sectionRecordCounts[String(section.id)] || 0;
    },

    getAnswersForEnabledParentCategory(parent) {
      if (!parent) {
        return [];
      }

      return this.visibleAnswers.filter(answer => {
        const answerParent = this.findEnabledParentCategoryMeta(answer);

        if (answerParent) {
          return this.sameText(answerParent.title, parent.title)
              || this.sameText(answerParent.key, parent.key)
              || (answerParent.id && parent.id && String(answerParent.id) === String(parent.id));
        }

        const fallbackParent = this.normaliseText(
            answer.parent
            || answer.regulatory_key
            || answer.regulatoryKey
            || answer.business_key
            || answer.regulatory_key_name
            || ''
        );

        return this.sameText(fallbackParent, parent.title) || this.sameText(fallbackParent, parent.key);
      });
    },

    getAnswersForEnabledBusinessFunction(section, parent = null) {
      if (!section) {
        return [];
      }

      return this.visibleAnswers.filter(answer => {
        const sectionMeta = this.findEnabledBusinessFunctionMeta(answer);

        if (sectionMeta) {
          const sectionMatches = this.sameText(sectionMeta.title, section.title)
              || this.sameText(sectionMeta.key, section.key)
              || (sectionMeta.id && section.id && String(sectionMeta.id) === String(section.id));

          if (!sectionMatches) {
            return false;
          }

          if (!parent) {
            return true;
          }

          return this.sameText(sectionMeta.parentTitle, parent.title)
              || this.sameText(sectionMeta.parentKey, parent.key)
              || (sectionMeta.parentId && parent.id && String(sectionMeta.parentId) === String(parent.id));
        }

        const fallbackSection = this.normaliseText(
            answer.section
            || answer.business_function
            || answer.businessFunction
            || answer.compliance_module
            || answer.question_section
            || ''
        );

        const fallbackParent = this.normaliseText(
            answer.parent
            || answer.regulatory_key
            || answer.regulatoryKey
            || answer.business_key
            || answer.regulatory_key_name
            || ''
        );

        const sectionMatches = this.sameText(fallbackSection, section.title) || this.sameText(fallbackSection, section.key);
        const parentMatches = !parent || this.sameText(fallbackParent, parent.title) || this.sameText(fallbackParent, parent.key);

        return sectionMatches && parentMatches;
      });
    },

    getActionTrackerScore(answers) {
      if (!answers || !answers.length) {
        return 0;
      }

      const correct = answers.filter(answer => this.isCorrectlyAnswered(answer)).length;

      return this.percent(correct, answers.length);
    },

    findEnabledParentBySection(section) {
      if (!section) {
        return null;
      }

      return this.allEnabledParentCategoryTree.find(parent => {
        return (parent.children || []).some(child => {
          return (section.id && child.id && String(section.id) === String(child.id))
              || (section.key && child.key && this.sameText(section.key, child.key))
              || (section.title && child.title && this.sameText(section.title, child.title));
        });
      }) || null;
    },

    findEnabledBusinessFunctionMeta(answer) {
      const answerSectionId = answer.section_id || answer.question_section_id || answer.sectionId || answer.questionSectionId || null;
      const answerSectionKey = this.normaliseText(answer.section_key || answer.sectionKey || answer.question_section_key || answer.questionSectionKey || '');
      const answerSectionTitle = this.normaliseText(answer.section || answer.section_title || answer.sectionTitle || answer.business_function || answer.businessFunction || answer.compliance_module || answer.question_section || '');

      return this.allEnabledBusinessFunctionSections.find(section => {
        if (answerSectionId && section.id && String(section.id) === String(answerSectionId)) {
          return true;
        }

        if (answerSectionKey && section.key && this.sameText(section.key, answerSectionKey)) {
          return true;
        }

        return answerSectionTitle && section.title && this.sameText(section.title, answerSectionTitle);
      }) || null;
    },

    findEnabledParentCategoryMeta(answer) {
      const sectionMeta = this.findEnabledBusinessFunctionMeta(answer);

      if (sectionMeta) {
        return this.allEnabledParentCategoryTree.find(parent => {
          return (sectionMeta.parentId && parent.id && String(sectionMeta.parentId) === String(parent.id))
              || (sectionMeta.parentKey && parent.key && this.sameText(sectionMeta.parentKey, parent.key))
              || (sectionMeta.parentTitle && parent.title && this.sameText(sectionMeta.parentTitle, parent.title));
        }) || null;
      }

      const answerParentId = answer.parent_id || answer.parentId || answer.regulatory_key_id || answer.regulatoryKeyId || null;
      const answerParentKey = this.normaliseText(answer.regulatory_key_key || answer.regulatoryKeyKey || answer.parent_key || answer.parentKey || '');
      const answerParentTitle = this.normaliseText(answer.parent || answer.parent_title || answer.parentTitle || answer.regulatory_key || answer.regulatoryKey || answer.business_key || answer.regulatory_key_name || '');

      return this.allEnabledParentCategoryTree.find(parent => {
        if (answerParentId && parent.id && String(parent.id) === String(answerParentId)) {
          return true;
        }

        if (answerParentKey && parent.key && this.sameText(parent.key, answerParentKey)) {
          return true;
        }

        return answerParentTitle && parent.title && this.sameText(parent.title, answerParentTitle);
      }) || null;
    },

    getEnabledSectionScore(answer) {
      const sectionMeta = this.findEnabledBusinessFunctionMeta(answer);

      return sectionMeta ? sectionMeta.score : null;
    },

    buildRegulatoryKeyPerformance(answers) {
      const groups = {};

      answers.forEach(answer => {
        const keyName = this.getRegulatoryKey(answer);
        const moduleName = this.getBusinessFunction(answer);
        const enabledSectionScore = this.getEnabledSectionScore(answer);
        const moduleKey = this.normaliseText(
            answer.section_id
            || answer.question_section_id
            || answer.section_key
            || answer.sectionKey
            || moduleName
        );

        if (!groups[keyName]) {
          groups[keyName] = {
            name: keyName,
            total: 0,
            correct: 0,
            modules: {}
          };
        }

        groups[keyName].total += 1;

        if (this.isCorrectlyAnswered(answer)) {
          groups[keyName].correct += 1;
        }

        if (!groups[keyName].modules[moduleKey]) {
          groups[keyName].modules[moduleKey] = {
            name: moduleName,
            total: 0,
            correct: 0,
            enabledScore: enabledSectionScore
          };
        }

        groups[keyName].modules[moduleKey].total += 1;

        if (groups[keyName].modules[moduleKey].enabledScore === null && enabledSectionScore !== null) {
          groups[keyName].modules[moduleKey].enabledScore = enabledSectionScore;
        }

        if (this.isCorrectlyAnswered(answer)) {
          groups[keyName].modules[moduleKey].correct += 1;
        }
      });

      return Object.values(groups).map(group => {
        const modules = Object.values(group.modules).map(module => {
          const fallbackScore = this.percent(module.correct, module.total);

          return {
            name: module.name,
            score: module.enabledScore !== null ? module.enabledScore : fallbackScore,
            actionTrackerScore: fallbackScore,
            total: module.total
          };
        }).sort((a, b) => a.score - b.score);

        const moduleScores = modules
            .map(module => module.score)
            .filter(score => score !== null && score !== undefined);

        return {
          name: group.name,
          description: this.buildRegulatoryDescription(modules),
          score: moduleScores.length ? Math.round(moduleScores.reduce((sum, score) => sum + score, 0) / moduleScores.length) : this.percent(group.correct, group.total),
          total: group.total,
          modules
        };
      }).sort((a, b) => b.score - a.score);
    },

    buildBusinessFunctionPerformance(answers) {
      const groups = {};

      answers.forEach(answer => {
        const name = this.getBusinessFunction(answer);
        const enabledSectionScore = this.getEnabledSectionScore(answer);
        const groupKey = this.normaliseText(
            answer.section_id
            || answer.question_section_id
            || answer.section_key
            || answer.sectionKey
            || name
        );

        if (!groups[groupKey]) {
          groups[groupKey] = {
            name,
            total: 0,
            correct: 0,
            enabledScore: enabledSectionScore
          };
        }

        groups[groupKey].total += 1;

        if (groups[groupKey].enabledScore === null && enabledSectionScore !== null) {
          groups[groupKey].enabledScore = enabledSectionScore;
        }

        if (this.isCorrectlyAnswered(answer)) {
          groups[groupKey].correct += 1;
        }
      });

      return Object.values(groups).map(group => {
        const fallbackScore = this.percent(group.correct, group.total);

        return {
          name: group.name,
          total: group.total,
          score: group.enabledScore !== null ? group.enabledScore : fallbackScore,
          actionTrackerScore: fallbackScore
        };
      }).sort((a, b) => b.score - a.score);
    },

    buildOutstandingAction(answer) {
      return {
        questionId: answer.question_id || null,
        regulatoryKey: this.getRegulatoryKey(answer),
        businessFunction: this.getBusinessFunction(answer),
        question: this.getQuestionText(answer),
        answer: this.getAnswerLabel(answer),
        severity: this.getSeverityLabel(answer),
        targetRemedy: this.getTargetRemedyValue(answer),
        resourceTitle: this.getResourceTitle(answer),
        resourceUrl: this.getResourceUrl(answer),
        priority: this.outstandingPriority(answer),
        // The factors behind `priority`, so the AI can explain the ranking.
        overdue: this.isOverdue(answer),
        missingTargetRemedy: !this.hasTargetRemedy(answer),
        missingNotes: !this.hasNotes(answer),
        missingDocuments: this.getDocumentsCount(answer) === 0
      };
    },

    getRegulatoryKey(answer) {
      const parentMeta = this.findEnabledParentCategoryMeta(answer);

      return this.normaliseText(
          (parentMeta && parentMeta.title)
          || answer.parent_title
          || answer.parentTitle
          || answer.parent
          || answer.regulatory_key_title
          || answer.regulatoryKeyTitle
          || answer.regulatory_key
          || answer.regulatoryKey
          || answer.business_key
          || answer.regulatory_key_name
          || 'Unassigned Regulatory Key'
      );
    },

    getBusinessFunction(answer) {
      const sectionMeta = this.findEnabledBusinessFunctionMeta(answer);

      return this.normaliseText(
          (sectionMeta && sectionMeta.title)
          || answer.section_title
          || answer.sectionTitle
          || answer.section
          || answer.business_function
          || answer.businessFunction
          || answer.compliance_module
          || answer.question_section
          || 'Unassigned Business Function'
      );
    },

    getQuestionText(answer) {
      return this.normaliseText(
          answer.question_action
          || answer.question
          || answer.question_text
          || answer.text
          || 'Question text unavailable'
      );
    },

    getAnswerLabel(answer) {
      const label = answer.answer
          || answer.your_answer
          || answer.answer_option
          || answer.answer_text
          || '';

      return this.normaliseText(label) || 'Blank / No response';
    },

    getSeverityLabel(answer) {
      return this.normaliseText(
          answer.severity_label
          || answer.severity_name
          || answer.severity
          || answer._severityTitle
          || ''
      );
    },

    getTargetRemedyValue(answer) {
      return answer.target_remedy
          || answer.target_remedy_date
          || answer.remedy_date
          || answer.target_date
          || null;
    },

    getResourceTitle(answer) {
      return this.normaliseText(
          answer.fca_resource_title
          || answer.resource_title
          || answer.guidance_title
          || answer.fca_resource
          || ''
      );
    },

    getResourceUrl(answer) {
      return answer.fca_resource_url
          || answer.resource_url
          || answer.guidance_url
          || answer.fca_url
          || null;
    },

    getNotesCount(answer) {
      if (Array.isArray(answer.notes)) {
        return answer.notes.length;
      }

      // Backend sends `notes` as a raw SQL COUNT(*) (a number, or a
      // numeric string depending on the DB driver) — not an array.
      if (
          answer.notes !== null
          && answer.notes !== undefined
          && answer.notes !== ''
          && !Number.isNaN(Number(answer.notes))
      ) {
        return Number(answer.notes);
      }

      if (Number(answer.notes_count) > 0) {
        return Number(answer.notes_count);
      }

      if (typeof answer.note === 'string') {
        return answer.note.trim() ? 1 : 0;
      }

      return 0;
    },

    getDocumentsCount(answer) {
      if (Array.isArray(answer.documents)) {
        return answer.documents.length;
      }

      if (Array.isArray(answer.files)) {
        return answer.files.length;
      }

      // Backend sends `documents` as a raw SQL COUNT(*) (a number, or a
      // numeric string depending on the DB driver) — not an array.
      if (
          answer.documents !== null
          && answer.documents !== undefined
          && answer.documents !== ''
          && !Number.isNaN(Number(answer.documents))
      ) {
        return Number(answer.documents);
      }

      if (Number(answer.documents_count) > 0) {
        return Number(answer.documents_count);
      }

      if (Number(answer.files_count) > 0) {
        return Number(answer.files_count);
      }

      return 0;
    },

    // Signed off: the action's status was set to Completed (needs a person
    // responsible, all checkpoints ticked and an owner/admin).
    isCompleted(answer) {
      const status = this.normaliseText(answer.status).toLowerCase();

      return status === 'completed'
          || status === 'complete'
          || answer.completed === true
          || !!answer.completed_at;
    },

    // Resolved: answered positively or signed off. Anything else is outstanding.
    isResolved(answer) {
      return this.isCompleted(answer) || this.isCorrectlyAnswered(answer);
    },

    // Fixed to a positive answer in the Action Tracker but nobody has set the
    // status to Completed yet. Initially correct answers never were actions.
    isAwaitingSignOff(answer) {
      return !this.isCompleted(answer)
          && !this.isInitiallyCorrect(answer)
          && this.isCorrectlyAnswered(answer);
    },

    isInitiallyCorrect(answer) {
      return answer.initially_correct === true || Number(answer.initially_correct) === 1;
    },

    getPartialScore(answer) {
      const maxScore = Number(answer.max_score);

      if (!maxScore || Number.isNaN(maxScore)) {
        return null;
      }

      const value = Number(answer.value);

      if (Number.isNaN(value)) {
        return null;
      }

      return (Math.max(value, 0) / maxScore) * 100;
    },

    isCorrectlyAnswered(answer) {
      const label = this.getAnswerLabel(answer).toLowerCase();

      if (
          label.includes('below the required level')
          || label.includes('below required')
          || label.includes('do not have')
          || label.includes('we are unsure')
          || label.includes('unsure')
          || label === 'no'
          || label.includes('plan to have')
      ) {
        return false;
      }

      if (answer.initially_correct === true || answer.initially_correct === 1) {
        return true;
      }

      if (Number(answer.value) >= 3) {
        return true;
      }

      return label === 'yes'
          || label === 'we have this'
          || label.includes('at or above the required level')
          || label.includes('above the required level')
          || label.includes('suitable for');
    },

    isBlankAnswer(answer) {
      const label = this.getAnswerLabel(answer).toLowerCase();

      return label === 'blank / no response'
          || label === '(blank / no response)'
          || label === '';
    },

    hasNotes(answer) {
      return this.getNotesCount(answer) > 0;
    },

    hasTargetRemedy(answer) {
      return !!this.getTargetRemedyValue(answer);
    },

    isOverdue(answer) {
      if (this.isResolved(answer)) {
        return false;
      }

      const remedyDate = this.parseDate(this.getTargetRemedyValue(answer));

      if (!remedyDate) {
        return false;
      }

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      return remedyDate < today;
    },

    parseDate(value) {
      if (!value) {
        return null;
      }

      if (value instanceof Date) {
        return value;
      }

      const stringValue = String(value).trim();

      const europeanDate = stringValue.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);

      if (europeanDate) {
        return new Date(
            Number(europeanDate[3]),
            Number(europeanDate[2]) - 1,
            Number(europeanDate[1])
        );
      }

      const parsed = new Date(stringValue);

      if (Number.isNaN(parsed.getTime())) {
        return null;
      }

      return parsed;
    },

    outstandingPriority(answer) {
      let priority = 0;

      if (this.isOverdue(answer)) {
        priority += 100;
      }

      const severity = this.getSeverityLabel(answer).toLowerCase();

      if (severity.includes('high') || severity.includes('red')) {
        priority += 50;
      }

      if (severity.includes('medium') || severity.includes('amber')) {
        priority += 25;
      }

      if (!this.isCorrectlyAnswered(answer)) {
        priority += 20;
      }

      if (!this.hasTargetRemedy(answer)) {
        priority += 10;
      }

      if (!this.hasNotes(answer)) {
        priority += 5;
      }

      if (this.getDocumentsCount(answer) === 0) {
        priority += 5;
      }

      return priority;
    },

    answerOptionClass(label) {
      const normalisedLabel = String(label).toLowerCase();

      if (
          normalisedLabel.includes('blank')
          || normalisedLabel === 'no'
          || normalisedLabel.includes('do not have')
          || normalisedLabel.includes('below required')
          || normalisedLabel.includes('below the required level')
      ) {
        return 'risk';
      }

      if (
          normalisedLabel.includes('work in progress')
          || normalisedLabel.includes('unsure')
          || normalisedLabel.includes('plan to have')
          || normalisedLabel.includes('not applicable')
      ) {
        return 'warning';
      }

      return 'good';
    },

    scoreClass(score) {
      if (score >= 75) {
        return 'good';
      }

      if (score >= 50) {
        return 'warning';
      }

      return 'risk';
    },

    severityClass(severity) {
      const label = String(severity || '').toLowerCase();

      if (label.includes('high') || label.includes('red')) {
        return 'risk';
      }

      if (label.includes('medium') || label.includes('amber')) {
        return 'warning';
      }

      return 'good';
    },

    getRagClass(answer) {
      return this.severityClass(this.getSeverityLabel(answer));
    },

    buildRagCounts(answers) {
      const counts = { red: 0, amber: 0, green: 0 };

      answers.forEach(answer => {
        const ragClass = this.getRagClass(answer);

        if (ragClass === 'risk') {
          counts.red += 1;
        } else if (ragClass === 'warning') {
          counts.amber += 1;
        } else {
          counts.green += 1;
        }
      });

      return counts;
    },

    buildRegulatoryDescription(modules) {
      if (!modules.length) {
        return 'No related business functions available';
      }

      return modules
          .slice(0, 4)
          .map(module => module.name)
          .join(', ');
    },

    chartBarHeight(value, maxValue) {
      if (!value || !maxValue) {
        return '0%';
      }

      const percentage = Math.round((value / maxValue) * 100);

      return `${Math.max(8, Math.min(100, percentage))}%`;
    },

    percent(value, total, decimals = 0) {
      if (!total) {
        return 0;
      }

      return Number(((value / total) * 100).toFixed(decimals));
    },

    formatSectionScore(value) {
      return value === null || value === undefined ? '—' : `${value}%`;
    },

    formatQuestionScore(value) {
      return value === null || value === undefined ? '—' : `${Number(value).toFixed(1)}%`;
    },

    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-GB');
    },

    formatDisplayValue(value) {
      if (typeof value === 'number') {
        return this.formatNumber(value);
      }

      return value;
    },

    sameText(firstValue, secondValue) {
      return this.normaliseText(firstValue).toLowerCase() === this.normaliseText(secondValue).toLowerCase();
    },

    normaliseComparableText(value) {
      return this.normaliseText(value).toLowerCase();
    },

    normaliseText(value) {
      if (value === null || value === undefined) {
        return '';
      }

      if (typeof value === 'object') {
        if (value.name) {
          return String(value.name).trim();
        }

        if (value.title) {
          return String(value.title).trim();
        }

        if (value.label) {
          return String(value.label).trim();
        }

        return '';
      }

      return String(value).trim();
    }
  }
};
</script>

<style scoped>
.ai-analytics-panel {
  background: #fff;
  border: 0;
  border-radius: 8px;
  padding: 20px;
  box-shadow: none;
  color: #111;
  font-size: 13px;
}

.ai-report-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 14px;
}

.ai-report-header h3 {
  margin: 0;
  color: #111;
  font-size: 24px;
  font-weight: 900;
  letter-spacing: 1px;
  text-transform: uppercase;
  line-height: 1.15;
}

.subtitle {
  margin-top: 5px;
  color: #777;
  font-size: 12px;
}

.btn-ai-secondary {
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  background: #fff;
  color: #333;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
  white-space: nowrap;
}

.btn-ai-secondary i {
  margin-right: 5px;
}

.drill-breadcrumb {
  margin-bottom: 18px;
  color: #777;
  font-size: 12px;
}

.drill-breadcrumb button {
  border: 0;
  background: transparent;
  padding: 0;
  color: #2b7ecb;
  font-weight: 700;
}

.drill-breadcrumb button.active {
  color: #111;
}

.drill-breadcrumb strong {
  color: #111;
}

.ai-section {
  border-top: 1px solid #e1e1e1;
  padding-top: 14px;
  margin-bottom: 22px;
}

.summary-section {
  border-top: 0;
  padding-top: 0;
}

.section-title {
  margin-bottom: 12px;
  color: #111;
  font-size: 14px;
  font-weight: 900;
}

.block-explainer {
  background: #f4f9fc;
  border-left: 4px solid #39aa94;
  padding: 10px 12px;
  margin-bottom: 14px;
  color: #333;
  font-size: 12px;
  line-height: 1.45;
}

.block-explainer div {
  margin-bottom: 4px;
}

.block-explainer div:last-child {
  margin-bottom: 0;
}

.scope-banner {
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f4f9fc;
  border-left: 5px solid #39aa94;
  padding: 12px 14px;
  margin-bottom: 18px;
}

.scope-banner strong {
  display: block;
  color: #111;
  font-size: 18px;
  font-weight: 900;
  margin-bottom: 3px;
}

.scope-banner span {
  color: #555;
  font-size: 12px;
}

.scope-count {
  color: #111;
  font-size: 12px;
  font-weight: 900;
  white-space: nowrap;
}

.ai-summary-row {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.summary-card {
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 12px;
  background: #fff;
}

.summary-label {
  color: #666;
  font-size: 11px;
  margin-bottom: 4px;
}

.summary-value {
  color: #111;
  font-size: 22px;
  font-weight: 900;
  line-height: 1.2;
}

.summary-status {
  margin-top: 4px;
  font-size: 11px;
  font-weight: 700;
}

.summary-calculation {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #eeeeee;
  color: #777;
  font-size: 10px;
}

.status-good {
  color: #39aa94;
}

.status-warning {
  color: #d79720;
}

.status-risk {
  color: #f32929;
}

.ai-alert-title {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  font-size: 22px;
  font-weight: 900;
  color: #111;
}

.red-dot {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #ff2222;
  margin-right: 8px;
  display: inline-block;
}

.clickable {
  cursor: pointer;
}

.regulatory-row {
  border: 1px solid #e1e1e1;
  border-radius: 4px;
  margin-bottom: 12px;
  background: #fff;
  overflow: hidden;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.regulatory-row:hover,
.module-item:hover,
.business-function-card:hover {
  border-color: #39aa94;
  box-shadow: 0 2px 8px rgba(57, 170, 148, 0.15);
}

.regulatory-main {
  display: flex;
  justify-content: space-between;
  padding: 12px;
  background: #f4f9fc;
  border-bottom: 1px solid #e1e1e1;
}

.regulatory-main strong {
  display: block;
  color: #111;
  font-size: 14px;
  font-weight: 900;
}

.regulatory-main span {
  display: block;
  margin-top: 3px;
  color: #555;
  font-size: 12px;
}

.score-value {
  color: #111;
  font-size: 20px;
  font-weight: 900;
}

.score-bar,
.mini-bar {
  background: #eceff1;
  overflow: hidden;
}

.score-bar {
  height: 7px;
}

.score-fill {
  height: 7px;
}

.module-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
}

.module-item {
  padding: 10px 12px;
  border-right: 1px solid #eee;
  border-bottom: 1px solid #eee;
}

.module-name {
  color: #111;
  font-size: 11px;
  font-weight: 700;
  min-height: 30px;
}

.module-score {
  margin: 4px 0;
  color: #666;
  font-size: 11px;
}

.module-meta {
  margin-top: 5px;
  color: #888;
  font-size: 10px;
}

.mini-bar {
  height: 5px;
}

.mini-fill {
  height: 5px;
}

.good {
  background: #39aa94;
}

.warning {
  background: #f0ad4e;
}

.risk {
  background: #f32929;
}

.action-section {
  border-top: 4px solid #5d4bd8;
  padding: 14px !important;
  background: #fbfbff;
  border-radius: 4px;
  font-size: 12px !important;
  line-height: 1.4 !important;
}

.action-title-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px !important;
  color: #111;
  font-size: 14px !important;
  font-weight: 900;
  line-height: 1.25 !important;
}

.action-title-row span {
  display: block;
  color: #111;
  font-size: 14px !important;
  font-weight: 900;
  line-height: 1.25 !important;
}

.btn-regenerate-ai {
  border: 1px solid #d7d7d7 !important;
  background: #fff !important;
  color: #333 !important;
  border-radius: 4px !important;
  padding: 5px 8px !important;
  font-size: 10px !important;
  font-weight: 800 !important;
  line-height: 1 !important;
  height: auto !important;
  min-height: 0 !important;
  box-shadow: none !important;
  text-shadow: none !important;
}

.btn-regenerate-ai:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.btn-regenerate-ai i {
  margin-right: 4px;
}

.ai-insight-columns {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
}

.ai-loading-state {
  padding: 14px;
  background: #fff;
  border: 1px dashed #d8d3ff;
  border-radius: 4px;
  color: #555;
  font-size: 12px !important;
}

.ai-loading-state > i {
  color: #5d4bd8;
  margin-right: 6px;
}

.ai-loading-state strong {
  display: inline-block;
  color: #111;
  font-size: 12px !important;
  font-weight: 900;
}

.ai-loading-state p {
  margin: 6px 0 12px;
  color: #666;
  font-size: 12px !important;
  line-height: 1.4 !important;
}

.ai-skeleton-line {
  height: 8px;
  background: linear-gradient(90deg, #eeeeee 0%, #f7f7f7 50%, #eeeeee 100%);
  border-radius: 10px;
  margin-bottom: 8px;
  animation: aiSkeletonPulse 1.1s ease-in-out infinite;
}

.ai-skeleton-line.short {
  width: 70%;
}

.ai-error-state {
  padding: 12px;
  background: #fff3f3;
  border-left: 4px solid #f32929;
  color: #9e1c1c;
  font-size: 12px !important;
  font-weight: 700;
  line-height: 1.4 !important;
}

.ai-error-state i {
  margin-right: 5px;
}

@keyframes aiSkeletonPulse {
  0% {
    opacity: 0.55;
  }

  50% {
    opacity: 1;
  }

  100% {
    opacity: 0.55;
  }
}

.scope-kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 22px;
}

.scope-kpi-card {
  border: 1px solid #e1e1e1;
  background: #fff;
  border-radius: 4px;
  padding: 12px;
}

.scope-kpi-card span {
  display: block;
  color: #666;
  font-size: 11px;
  margin-bottom: 4px;
}

.scope-kpi-card strong {
  display: block;
  color: #111;
  font-size: 20px;
  font-weight: 900;
  margin-bottom: 2px;
}

.scope-kpi-card em {
  color: #777;
  font-size: 10px;
  font-style: normal;
}

.business-function-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 11px;
}

.business-function-card {
  border: 1px solid #dcdcdc;
  background: #fff;
  border-radius: 4px;
  min-height: 95px;
  padding: 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.business-function-card strong {
  display: block;
  color: #111;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
}

.business-function-card span {
  display: block;
  color: #777;
  font-size: 11px;
  margin-top: 4px;
}

.circle-score {
  min-width: 44px;
  height: 44px;
  border-radius: 50%;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 900;
  margin-left: 10px;
}

.dynamic-steer {
  display: flex;
  border-bottom: 1px solid #ececec;
  padding: 14px 0;
}

.dynamic-steer:last-child {
  border-bottom: 0;
}

.steer-dot {
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  margin-right: 10px;
  margin-top: 1px;
}

.steer-content strong {
  display: block;
  color: #111;
  font-size: 14px;
  font-weight: 900;
  margin-bottom: 6px;
}

.steer-content p {
  margin: 0 0 4px;
  color: #222;
  font-size: 13px;
  line-height: 1.45;
}

.steer-content p span {
  font-weight: 900;
}

.returned-action-banner {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin-bottom: 14px;
  padding: 10px 12px;
  border-left: 4px solid;
  font-size: 13px;
  line-height: 1.45;
}

.returned-action-banner > i {
  margin-top: 3px;
}

.returned-action-banner div {
  flex: 1;
}

.returned-action-banner strong,
.returned-action-banner span {
  display: block;
}

.returned-action-hint {
  margin-top: 2px;
  font-size: 12px;
  color: #555;
}

.returned-action-signed-off {
  background: #eaf7f0;
  border-color: #2e9e5b;
  color: #1f6b3e;
}

.returned-action-resolved {
  background: #f3faf7;
  border-color: #39aa94;
  color: #1d6f5f;
}

.returned-action-outstanding {
  background: #fff7e8;
  border-color: #f0ad4e;
  color: #8a5a12;
}

.returned-action-close {
  border: 0;
  background: none;
  color: inherit;
  cursor: pointer;
  padding: 0 2px;
}

.ai-generated-at {
  margin: -4px 0 10px;
  color: #777;
  font-size: 11px;
  line-height: 1.4;
}

.no-records {
  opacity: 0.6;
}

.no-records-note {
  display: block;
  margin-top: 4px;
  color: #777;
  font-size: 11px;
  font-style: italic;
}

.steer-empty {
  margin: 10px 0 0;
  color: #666;
  font-size: 13px;
}

.awaiting-sign-off {
  margin-top: 16px;
  padding: 12px 14px 4px;
  background: #f3faf7;
  border-left: 4px solid #39aa94;
}

.awaiting-sign-off-title {
  color: #1d6f5f;
  font-size: 14px;
  font-weight: 900;
}

.awaiting-sign-off-title i {
  margin-right: 6px;
}

.awaiting-sign-off-hint {
  margin: 6px 0 4px;
  color: #333;
  font-size: 12px;
  line-height: 1.45;
}

.awaiting-sign-off-dot {
  background: #39aa94;
}

.awaiting-sign-off-cta {
  color: #1d6f5f;
}

.steer-action-link {
  color: #2b7ecb;
  text-decoration: none;
}

.steer-action-link:hover {
  text-decoration: underline;
}

.steer-open-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 0;
  border: 0;
  background: none;
  color: #2b7ecb;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.steer-open-action:hover {
  text-decoration: underline;
}

.awaiting-sign-off-more {
  margin: 0 0 10px;
  color: #666;
  font-size: 12px;
}

.resource-link {
  display: inline-block;
  margin-top: 6px;
  color: #2b7ecb;
  font-size: 12px;
  text-decoration: none;
}

.resource-link i {
  color: #f1c40f;
  margin-right: 5px;
}

.two-column-table {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
}

.two-column-table h5 {
  margin: 0 0 8px;
  color: #111;
  font-size: 13px;
  font-weight: 900;
}

.outstanding-action {
  display: flex;
  border-bottom: 1px solid #e8e8e8;
  padding: 10px 0;
}

.outstanding-action:last-child {
  border-bottom: 0;
}

.small-severity-dot {
  width: 11px;
  height: 11px;
  min-width: 11px;
  border-radius: 50%;
  margin-right: 8px;
  margin-top: 3px;
}

.outstanding-action strong {
  display: block;
  color: #111;
  font-size: 12px;
  font-weight: 900;
}

.outstanding-action p {
  margin: 3px 0;
  color: #333;
  font-size: 12px;
  line-height: 1.4;
}

.outstanding-action em {
  color: #777;
  font-size: 11px;
  font-style: normal;
}

.ai-empty {
  padding: 30px;
  text-align: center;
  color: #555;
  font-size: 14px;
  background: #f4f9fc;
  border-left: 4px solid #39aa94;
}


/* Chatbot welcome */
.chatbot-intro {
  display: flex;
  align-items: flex-start;
  background: #f4f9fc;
  border-left: 5px solid #39aa94;
  padding: 12px 14px;
  margin-bottom: 18px;
  color: #333;
  font-size: 12px;
  line-height: 1.45;
}

.chatbot-intro img {
  color: #39aa94;
  width: 24px;
  margin-right: 10px;
  margin-top: 1px;
}

/* Dashboard layout */
.ai-dashboard-layout {
  align-items: flex-start;
}

.ai-centre-column .ai-section:first-child,
.ai-right-column .ai-section:first-child {
  margin-top: 0;
}

/* Performance summary */
.performance-highlight-section {
  border-top: 4px solid #39aa94;
  padding: 14px;
  background: #ffffff;
  border-radius: 4px;
  border-left: 1px solid #e1e1e1;
  border-right: 1px solid #e1e1e1;
  border-bottom: 1px solid #e1e1e1;
}

.performance-card-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 10px;
  margin-bottom: 14px;
}

.performance-card {
  border: 1px solid #e1e1e1;
  background: #fff;
  border-radius: 4px;
  padding: 10px;
  min-height: 92px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.performance-card:hover {
  border-color: #39aa94;
  box-shadow: 0 2px 8px rgba(57, 170, 148, 0.15);
}

.performance-card span {
  display: block;
  color: #666;
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 5px;
  text-transform: uppercase;
}

.performance-card strong {
  display: block;
  color: #111;
  font-size: 12px;
  font-weight: 900;
  line-height: 1.35;
  min-height: 34px;
}

.performance-card em {
  display: block;
  margin-top: 6px;
  font-size: 13px;
  font-style: normal;
  font-weight: 900;
}

.mini-action-list {
  border-top: 1px solid #e8e8e8;
  padding-top: 10px;
}

.mini-action-item {
  display: flex;
  align-items: flex-start;
  margin-bottom: 7px;
}

.mini-action-item:last-child {
  margin-bottom: 0;
}

.mini-action-item span {
  width: 18px;
  height: 18px;
  min-width: 18px;
  border-radius: 50%;
  background: #39aa94;
  color: #fff;
  font-size: 10px;
  font-weight: 900;
  line-height: 18px;
  text-align: center;
  margin-right: 8px;
}

.mini-action-item p {
  margin: 0;
  color: #333;
  font-size: 12px;
  line-height: 1.4;
}

/* Diagnostic section */
.diagnostic-section {
  border-top: 4px solid #2b7ecb;
  padding: 14px;
  background: #ffffff;
  border-radius: 4px;
  border-left: 1px solid #e1e1e1;
  border-right: 1px solid #e1e1e1;
  border-bottom: 1px solid #e1e1e1;
}

.diagnostic-card-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 9px;
  margin-bottom: 14px;
}

.diagnostic-card {
  border: 1px solid #e5e5e5;
  background: #fff;
  border-radius: 4px;
  padding: 10px;
}

.diagnostic-card span {
  display: block;
  color: #666;
  font-size: 10px;
  font-weight: 700;
  margin-bottom: 4px;
}

.diagnostic-card strong {
  display: block;
  color: #111;
  font-size: 20px;
  font-weight: 900;
  line-height: 1.1;
}

.diagnostic-card em {
  display: block;
  margin-top: 4px;
  font-size: 10px;
  font-style: normal;
  font-weight: 800;
}

.diagnostic-card small {
  display: block;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid #eeeeee;
  color: #777;
  font-size: 10px;
}

/* Action point source links */
/* Action Tracker links */
.tracker-link-section {
  border-top: 4px solid #f0ad4e;
  padding: 14px;
  background: #fff;
  border-radius: 4px;
  border-left: 1px solid #e1e1e1;
  border-right: 1px solid #e1e1e1;
  border-bottom: 1px solid #e1e1e1;
}

@media (max-width: 991px) {
  .ai-summary-row,
  .scope-kpi-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .ai-report-header {
    display: block;
  }

  .btn-ai-secondary {
    margin-top: 12px;
  }

  .module-grid,
  .business-function-grid,
  .two-column-table {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 600px) {
  .ai-summary-row,
  .scope-kpi-grid {
    grid-template-columns: 1fr;
  }

  .ai-alert-title {
    font-size: 18px;
  }

  .ai-report-header h3 {
    font-size: 20px;
  }

  .scope-banner {
    display: block;
  }

  .scope-count {
    margin-top: 8px;
  }
}

/* Chart cards */
.chart-card {
  background: #ffffff;
  border: 1px solid #e5e5e5;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 14px;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.chart-header strong {
  display: block;
  color: #111;
  font-size: 13px;
  font-weight: 900;
  line-height: 1.3;
}

.chart-header span {
  display: block;
  color: #666;
  font-size: 11px;
  margin-top: 3px;
  line-height: 1.35;
}

.rag-breakdown-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
}

.rag-card {
  padding: 12px;
  border-radius: 4px;
  border: 1px solid #e5e5e5;
  text-align: center;
}

.rag-card.clickable {
  cursor: pointer;
}

.rag-card span {
  display: block;
  color: #666;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  margin-bottom: 4px;
}

.rag-card strong {
  display: block;
  font-size: 22px;
  font-weight: 900;
}

.rag-red {
  background: #fff3f3;
  border-color: #f5c2c2;
}

.rag-red strong {
  color: #f32929;
}

.rag-amber {
  background: #fff7e8;
  border-color: #f3ddb1;
}

.rag-amber strong {
  color: #f0ad4e;
}

.rag-green {
  background: #f0faf8;
  border-color: #bfe6dd;
}

.rag-green strong {
  color: #39aa94;
}

.doc-function-dropdown {
  position: relative;
}

.doc-function-dropdown-toggle {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  background: #fff;
  color: #111;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.doc-function-dropdown-toggle:hover {
  border-color: #39aa94;
}

.doc-function-dropdown-toggle i {
  color: #5d4bd8;
  font-size: 11px;
}

.doc-function-dropdown-list {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  max-height: 260px;
  overflow-y: auto;
  background: #fff;
  border: 1px solid #d7d7d7;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(17, 17, 17, 0.1);
  z-index: 20;
}

.doc-function-dropdown-item {
  padding: 10px 12px;
  border-bottom: 1px solid #f0f0f0;
}

.doc-function-dropdown-item:last-child {
  border-bottom: 0;
}

.doc-function-dropdown-item:hover {
  background: #f4f9fc;
}

.doc-function-dropdown-item strong {
  display: block;
  color: #111;
  font-size: 12px;
  font-weight: 900;
}

.doc-function-dropdown-item span {
  display: block;
  color: #777;
  font-size: 11px;
  margin-top: 2px;
}

.doc-function-dropdown-item em {
  display: block;
  margin-top: 4px;
  color: #39aa94;
  font-size: 11px;
  font-weight: 700;
  font-style: normal;
}

.ai-empty-small {
  padding: 10px;
  color: #777;
  font-size: 12px;
  background: #fff;
  border: 1px dashed #ddd;
  border-radius: 4px;
}

.grouped-bar-chart {
  display: flex;
  align-items: stretch;
  gap: 10px;
  min-height: 290px;
  overflow-x: auto;
  padding: 4px 0 10px;
  border-bottom: 1px solid #eeeeee;
}

.grouped-bar-item {
  min-width: 112px;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
}

.bar-pair {
  height: 150px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  gap: 6px;
  padding: 0 8px;
}

.chart-bar {
  width: 32px;
  min-height: 0;
  border: 0;
  border-radius: 6px 6px 0 0;
  color: #fff;
  padding: 0;
  position: relative;
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.chart-bar:hover {
  opacity: 0.85;
  transform: translateY(-2px);
}

.chart-bar.best {
  background: #39aa94;
}

.chart-bar.weakest {
  background: #f0ad4e;
}

.chart-bar span {
  position: absolute;
  left: 50%;
  top: -18px;
  transform: translateX(-50%);
  color: #111;
  font-size: 10px;
  font-weight: 900;
  white-space: nowrap;
}

.bar-caption {
  min-height: 78px;
  padding-top: 8px;
  text-align: center;
}

.bar-caption strong {
  display: block;
  color: #111;
  font-size: 11px;
  font-weight: 900;
  line-height: 1.25;
  margin-bottom: 4px;
}

.bar-caption small {
  display: block;
  color: #777;
  font-size: 9px;
  line-height: 1.25;
  margin-bottom: 2px;
}

.chart-legend {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  padding-top: 10px;
  color: #555;
  font-size: 11px;
}

.chart-legend span {
  display: inline-flex;
  align-items: center;
}

.chart-legend i {
  width: 10px;
  height: 10px;
  border-radius: 2px;
  margin-right: 5px;
}

.legend-best {
  background: #39aa94;
}

.legend-weakest {
  background: #f0ad4e;
}

.simple-bar-chart {
  height: 190px;
  display: flex;
  align-items: flex-end;
  justify-content: space-around;
  gap: 12px;
  padding: 8px 6px 0;
  border-bottom: 1px solid #e5e5e5;
}

.simple-bar-item {
  flex: 1;
  min-width: 0;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  align-items: center;
}

.simple-bar-track {
  width: 100%;
  max-width: 58px;
  height: 135px;
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.simple-bar-fill {
  width: 42px;
  border-radius: 6px 6px 0 0;
  position: relative;
}

.simple-bar-fill.answered {
  background: #2b7ecb;
}

.simple-bar-fill.completed {
  background: #f0ad4e;
}

.simple-bar-fill.positive {
  background: #39aa94;
}

.simple-bar-fill span {
  position: absolute;
  left: 50%;
  top: -18px;
  transform: translateX(-50%);
  color: #111;
  font-size: 10px;
  font-weight: 900;
  white-space: nowrap;
}

.simple-bar-label {
  min-height: 38px;
  margin-top: 8px;
  color: #555;
  font-size: 10px;
  font-weight: 700;
  line-height: 1.2;
  text-align: center;
}


/* Right-column drill dashboard links */
.section-title-with-link,
.right-dashboard-link-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
  justify-content: space-between;
}

.right-dashboard-link-row {
  margin: -4px 0 12px;
}

.metric-link-wrapper {
  position: relative;
}

.inline-dashboard-link,
.standalone-dashboard-link {
  display: inline-flex;
  align-items: center;
  border: 1px solid #d7d7d7;
  background: #fff;
  color: #2b7ecb;
  border-radius: 4px;
  padding: 6px 9px;
  font-size: 11px;
  font-weight: 800;
  line-height: 1.1;
  text-align: left;
}

.standalone-dashboard-link {
  margin-bottom: 10px;
}

.inline-dashboard-link:hover,
.standalone-dashboard-link:hover {
  border-color: #39aa94;
  color: #111;
}

.inline-dashboard-link i,
.standalone-dashboard-link i {
  margin-right: 5px;
}

/* Metric dashboards */
.metric-dashboard-layout {
  margin-top: 8px;
}

.metric-dashboard-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: #f4f9fc;
  border-left: 5px solid #5d4bd8;
  padding: 14px;
  margin-bottom: 12px;
}

.metric-dashboard-header strong {
  display: block;
  color: #111;
  font-size: 18px;
  font-weight: 900;
  margin-bottom: 3px;
}

.metric-dashboard-header span {
  display: block;
  color: #555;
  font-size: 12px;
  line-height: 1.4;
}

.metric-dashboard-tabs {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 14px;
}

.metric-dashboard-tabs button {
  border: 1px solid #d7d7d7;
  background: #fff;
  color: #333;
  border-radius: 4px;
  padding: 7px 10px;
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
}

.metric-dashboard-tabs button.active,
.metric-dashboard-tabs button:hover {
  border-color: #5d4bd8;
  background: #fbfbff;
  color: #111;
}

.metric-detail-section {
  padding: 16px;
}

.metric-dashboard-card-grid {
  grid-template-columns: repeat(4, 1fr);
}

.large-chart-card {
  margin-bottom: 18px;
}

.large-simple-bar-chart {
  min-height: 260px;
}

.large-simple-bar-chart .simple-bar-track {
  height: 185px;
}

.compact-chart-card {
  margin-top: 10px;
}

.compact-simple-bar-chart {
  height: 155px;
}

.compact-simple-bar-chart .simple-bar-track {
  height: 100px;
}

.simple-bar-fill.blank,
.simple-bar-fill.no-notes {
  background: #9ca3af;
}

.simple-bar-fill.overdue,
.simple-bar-fill.outstanding {
  background: #f32929;
}

.simple-bar-fill.notes,
.simple-bar-fill.documents,
.simple-bar-fill.targeted {
  background: #2b7ecb;
}

.simple-bar-fill.good {
  background: #39aa94;
}

.simple-bar-fill.warning {
  background: #f0ad4e;
}

.simple-bar-fill.risk {
  background: #f32929;
}

.answer-options-bar-chart .simple-bar-item,
.notes-actions-bar-chart .simple-bar-item,
.target-remedy-bar-chart .simple-bar-item {
  min-width: 90px;
}

@media (max-width: 991px) {
  .metric-dashboard-card-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .metric-dashboard-header {
    display: block;
  }

  .metric-dashboard-header .btn-ai-secondary {
    margin-top: 12px;
  }
}

@media (max-width: 600px) {
  .metric-dashboard-card-grid {
    grid-template-columns: 1fr;
  }
}


.metric-ai-insights-section {
  margin-top: 18px;
  border-top: 4px solid #5d4bd8;
  background: #fbfbff;
}

.metric-ai-insights-section .block-explainer {
  margin-bottom: 14px;
}

</style>
